import React, { useState, useEffect, useContext } from 'react';
import { 
  Calendar, Clock, User, MapPin, Phone, Mail, Plus, Search, Filter, Edit, Trash2, X, 
  CheckCircle, AlertCircle, XCircle, Eye, ChevronLeft, ChevronRight, Save, Users,
  Stethoscope, Building2, FileText, Activity, AlertTriangle, MoreHorizontal, Settings,
  ToggleLeft, ToggleRight, RefreshCw, Shield, UserCheck, ClipboardCheck, UserPlus, Cloud
} from 'lucide-react';
import { AuthContext } from '../App';
import './AppointmentManagement.css';

const API_URL = process.env.REACT_APP_API_URL;

const ESTADOS_CITA = {
  programada: { label: 'Programada', color: '#3498db', bgColor: 'bg-blue-100', textColor: 'text-blue-800' },
  confirmada: { label: 'Confirmada', color: '#2ecc71', bgColor: 'bg-green-100', textColor: 'text-green-800' },
  en_curso: { label: 'En Curso', color: '#f39c12', bgColor: 'bg-yellow-100', textColor: 'text-yellow-800' },
  completada: { label: 'Completada', color: '#27ae60', bgColor: 'bg-green-200', textColor: 'text-green-900' },
  cancelada: { label: 'Cancelada', color: '#e74c3c', bgColor: 'bg-red-100', textColor: 'text-red-800' },
  no_asistio: { label: 'No Asistió', color: '#95a5a6', bgColor: 'bg-gray-200', textColor: 'text-gray-800' }
};

const ESTADOS_REGISTRO = {
  aprobada: { label: 'Aprobada', color: '#10b981', icon: '✓', bgColor: '#d1fae5' },
  pendiente_aprobacion: { label: 'Pendiente', color: '#f59e0b', icon: '⏳', bgColor: '#fef3c7' },
  rechazada: { label: 'Rechazada', color: '#ef4444', icon: '✗', bgColor: '#fee2e2' }
};

export default function AppointmentManagement() {
  const { user } = useContext(AuthContext);
  
  // Estados principales
  const [clienteActual, setClienteActual] = useState(null);
  const [citas, setCitas] = useState([]);
  const [citasPendientes, setCitasPendientes] = useState([]);
  const [pacientes, setPacientes] = useState([]);
  const [medicos, setMedicos] = useState([]);
  const [consultas, setConsultas] = useState([]);

  const [configTab, setConfigTab] = useState('general');
  const [calendarioEstado, setCalendarioEstado] = useState(null);
  const [showICloudModal, setShowICloudModal] = useState(false);
  const [icloudForm, setIcloudForm] = useState({ email: '', password: '' });
  
  // Estados de UI
  const [loading, setLoading] = useState(false);
  const [vistaActiva, setVistaActiva] = useState('calendario');
  const [fechaSeleccionada, setFechaSeleccionada] = useState(new Date());
  const [mesActual, setMesActual] = useState(new Date());
  
  // Modales
  const [showCitaModal, setShowCitaModal] = useState(false);
  const [showPacienteModal, setShowPacienteModal] = useState(false);
  const [showMedicoModal, setShowMedicoModal] = useState(false);
  const [showConsultaModal, setShowConsultaModal] = useState(false);
  const [showDetalleModal, setShowDetalleModal] = useState(false);
  const [showConflictoModal, setShowConflictoModal] = useState(false);
  const [showTodasCitasModal, setShowTodasCitasModal] = useState(false);
  const [showAprobarModal, setShowAprobarModal] = useState(false);
  const [showAprobarConCambiosModal, setShowAprobarConCambiosModal] = useState(false);
  const [showRechazarModal, setShowRechazarModal] = useState(false);
  
  const [citasDelDiaSeleccionado, setCitasDelDiaSeleccionado] = useState([]);
  const [fechaDiaSeleccionado, setFechaDiaSeleccionado] = useState(null);
  
  // Datos del formulario
  const [citaForm, setCitaForm] = useState({
    id: null, paciente_id: '', medico_id: '', consulta_id: '',
    fecha: '', hora_inicio: '', hora_fin: '', motivo: '', notas: '', estado: 'programada'
  });
  
  const [pacienteForm, setPacienteForm] = useState({
    id: null, nombre: '', apellidos: '', telefono: '', email: '', 
    dni: '', fecha_nacimiento: '', direccion: '', notas: ''
  });
  
  const [medicoForm, setMedicoForm] = useState({
    id: null, nombre: '', apellidos: '', especialidad: '', telefono: '', 
    email: '', num_colegiado: '', color: '#3cbf9a', activo: true
  });
  
  const [consultaForm, setConsultaForm] = useState({
    id: null, nombre: '', ubicacion: '', color: '#3cbf9a', activa: true
  });
  
  const [citaSeleccionada, setCitaSeleccionada] = useState(null);
  const [conflictoData, setConflictoData] = useState(null);
  
  // Nuevos estados para aprobación/rechazo
  const [motivoRechazo, setMotivoRechazo] = useState('');
  const [cambiosCita, setCambiosCita] = useState({
    fecha: '', hora_inicio: '', hora_fin: '', medico_id: '', consulta_id: ''
  });
  
  // Configuración
  const [autoRegistroEnabled, setAutoRegistroEnabled] = useState(false);
  const [requireCitaApproval, setRequireCitaApproval] = useState(true);
  const [guardandoConfiguracion, setGuardandoConfiguracion] = useState(false);
  
  // Filtros
  const [filtros, setFiltros] = useState({
    medico_id: '', consulta_id: '', estado: '', busqueda: '', fecha_desde: '', fecha_hasta: ''
  });
  
  // Estadísticas
  const [estadisticas, setEstadisticas] = useState(null);

  // Cargar información del cliente al iniciar
  useEffect(() => {
    if (user && user.clinica_matriz) {
      cargarInformacionCliente();
    }
  }, [user]);

  useEffect(() => {
    if (clienteActual) {
      cargarDatos();
    }
  }, [clienteActual]);

  useEffect(() => {
    if (clienteActual) {
      cargarCitas();
      if (vistaActiva === 'pendientes') {
        cargarCitasPendientes();
      }
    }
  }, [clienteActual, mesActual, filtros, vistaActiva]);

  useEffect(() => {
  if (clienteActual && vistaActiva === 'configuracion' && configTab === 'calendario') {
    cargarEstadoCalendario();
  }
}, [clienteActual, vistaActiva, configTab]);

useEffect(() => {
  const urlParams = new URLSearchParams(window.location.search);
  const calendarConnected = urlParams.get('calendar_connected');
  const error = urlParams.get('error');

  if (calendarConnected) {
    console.log('✅ Calendario conectado:', calendarConnected);
    alert(`✓ Calendario conectado correctamente`);
    window.history.replaceState({}, document.title, window.location.pathname);
    
    if (clienteActual) {
      cargarEstadoCalendario();
    }
  }

  if (error) {
    console.log('❌ Error de calendario:', error);
    alert('Error al conectar calendario');
    window.history.replaceState({}, document.title, window.location.pathname);
  }
}, []);

  const cargarInformacionCliente = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await fetch(
        `${API_URL}/api/clientes/${user.clinica_matriz}`,
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      if (response.ok) {
        const data = await response.json();
        
        // 🔍 AGREGAR ESTOS LOGS PARA DEBUGGEAR
        console.log('🔍 Data completa recibida:', data);
        console.log('🔍 Cliente:', data.cliente);
        console.log('🔍 auto_registro_enabled:', data.cliente?.auto_registro_enabled);
        console.log('🔍 Tipo de auto_registro_enabled:', typeof data.cliente?.auto_registro_enabled);
        
        setClienteActual(data.cliente);
        
        const autoRegistroValue = data.cliente.auto_registro_enabled === 1 || 
                                  data.cliente.auto_registro_enabled === "1" || 
                                  data.cliente.auto_registro_enabled === true;
        
        console.log('🔍 Valor a establecer en autoRegistroEnabled:', autoRegistroValue);
        
        setAutoRegistroEnabled(autoRegistroValue);
      } else {
        console.error('❌ Error en la respuesta:', response.status);
      }
    } catch (error) {
      console.error('❌ Error al cargar información del cliente:', error);
    } finally {
      setLoading(false);
    }
  };

  const cargarDatos = async () => {
    setLoading(true);
    try {
      await Promise.all([
        cargarPacientes(), 
        cargarMedicos(), 
        cargarConsultas(), 
        cargarCitas(), 
        cargarEstadisticas()
      ]);
    } catch (error) {
      console.error('Error al cargar datos:', error);
    } finally {
      setLoading(false);
    }
  };

  const cargarPacientes = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(
        `${API_URL}/api/citas/pacientes?cliente_id=${clienteActual.id}`,
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      if (response.ok) {
        const data = await response.json();
        setPacientes(data.pacientes);
      }
    } catch (error) {
      console.error('Error al cargar pacientes:', error);
    }
  };

  const cargarMedicos = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(
        `${API_URL}/api/citas/medicos?cliente_id=${clienteActual.id}`,
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      if (response.ok) {
        const data = await response.json();
        setMedicos(data.medicos);
      }
    } catch (error) {
      console.error('Error al cargar médicos:', error);
    }
  };

  const cargarConsultas = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(
        `${API_URL}/api/citas/consultas?cliente_id=${clienteActual.id}`,
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      if (response.ok) {
        const data = await response.json();
        setConsultas(data.consultas);
      }
    } catch (error) {
      console.error('Error al cargar consultas:', error);
    }
  };

  const cargarCitas = async () => {
    try {
      const token = localStorage.getItem('token');
      
      let params;
      if (vistaActiva === 'calendario') {
        const primerDia = new Date(mesActual.getFullYear(), mesActual.getMonth(), 1);
        const ultimoDia = new Date(mesActual.getFullYear(), mesActual.getMonth() + 1, 0);
        
        const formatearFecha = (fecha) => {
          const year = fecha.getFullYear();
          const month = String(fecha.getMonth() + 1).padStart(2, '0');
          const day = String(fecha.getDate()).padStart(2, '0');
          return `${year}-${month}-${day}`;
        };
        
        params = new URLSearchParams({
          cliente_id: clienteActual.id,
          fecha_inicio: formatearFecha(primerDia),
          fecha_fin: formatearFecha(ultimoDia)
        });
      } else {
        params = new URLSearchParams({
          cliente_id: clienteActual.id
        });
        
        if (filtros.fecha_desde) params.append('fecha_inicio', filtros.fecha_desde);
        if (filtros.fecha_hasta) params.append('fecha_fin', filtros.fecha_hasta);
      }
      
      if (filtros.medico_id) params.append('medico_id', filtros.medico_id);
      if (filtros.consulta_id) params.append('consulta_id', filtros.consulta_id);
      if (filtros.estado) params.append('estado', filtros.estado);
      
      const response = await fetch(
        `${API_URL}/api/citas/citas?${params.toString()}`,
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      
      if (response.ok) {
        const data = await response.json();
        setCitas(data.citas || []);
      }
    } catch (error) {
      console.error('Error al cargar citas:', error);
    }
  };

  const cargarCitasPendientes = async () => {
    try {
      const token = localStorage.getItem('token');
      // CORRECCIÓN: Cambiar de /citas/pendientes a /citas/citas/pendientes
      const response = await fetch(
        `${API_URL}/api/citas/citas/pendientes-aprobacion?cliente_id=${clienteActual.id}`,
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      if (response.ok) {
        const data = await response.json();
        setCitasPendientes(data.citas || []);
      }
    } catch (error) {
      console.error('Error al cargar citas pendientes:', error);
    }
  };

  const cargarEstadisticas = async () => {
    try {
      const token = localStorage.getItem('token');
      const mes = `${mesActual.getFullYear()}-${(mesActual.getMonth() + 1).toString().padStart(2, '0')}`;
      const response = await fetch(
        `${API_URL}/api/citas/citas/estadisticas?cliente_id=${clienteActual.id}&mes=${mes}`,
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      if (response.ok) {
        const data = await response.json();
        setEstadisticas(data.estadisticas);
      }
    } catch (error) {
      console.error('Error al cargar estadísticas:', error);
    }
  };

  // Funciones auxiliares para el calendario
  const cambiarMes = (delta) => {
    setMesActual(new Date(mesActual.getFullYear(), mesActual.getMonth() + delta, 1));
  };

  const irHoy = () => {
    const hoy = new Date();
    setMesActual(hoy);
    setFechaSeleccionada(hoy);
  };

  const obtenerDiasDelMes = () => {
    const year = mesActual.getFullYear();
    const month = mesActual.getMonth();
    const primerDia = new Date(year, month, 1);
    const ultimoDia = new Date(year, month + 1, 0);
    const dias = [];
    const primerDiaSemana = primerDia.getDay();
    
    for (let i = primerDiaSemana; i > 0; i--) {
      dias.push({ fecha: new Date(year, month, 1 - i), mesActual: false });
    }
    for (let i = 1; i <= ultimoDia.getDate(); i++) {
      dias.push({ fecha: new Date(year, month, i), mesActual: true });
    }
    const diasRestantes = 7 - (dias.length % 7);
    if (diasRestantes < 7) {
      for (let i = 1; i <= diasRestantes; i++) {
        dias.push({ fecha: new Date(year, month + 1, i), mesActual: false });
      }
    }
    return dias;
  };

  const obtenerCitasDelDia = (fecha) => {
    const year = fecha.getFullYear();
    const month = String(fecha.getMonth() + 1).padStart(2, '0');
    const day = String(fecha.getDate()).padStart(2, '0');
    const fechaStr = `${year}-${month}-${day}`;
    
    const citasDelDia = citas.filter(c => c.fecha === fechaStr)
      .sort((a, b) => a.hora_inicio.localeCompare(b.hora_inicio));
    
    return citasDelDia;
  };

  const esHoy = (fecha) => {
    const hoy = new Date();
    return fecha.toDateString() === hoy.toDateString();
  };

  const formatearFechaLocal = (fechaStr) => {
    const [year, month, day] = fechaStr.split('-');
    return new Date(year, month - 1, day).toLocaleDateString('es-ES');
  };

  const getColorCita = (cita) => {
    if (cita.medico_color) {
      return cita.medico_color;
    }
    if (cita.consulta_color) {
      return cita.consulta_color;
    }
    return ESTADOS_CITA[cita.estado].color;
  };

  const lightenColor = (color, percent = 40) => {
    const num = parseInt(color.replace("#", ""), 16);
    const amt = Math.round(2.55 * percent);
    const R = (num >> 16) + amt;
    const G = (num >> 8 & 0x00FF) + amt;
    const B = (num & 0x0000FF) + amt;
    return "#" + (0x1000000 + (R < 255 ? R < 1 ? 0 : R : 255) * 0x10000 +
      (G < 255 ? G < 1 ? 0 : G : 255) * 0x100 +
      (B < 255 ? B < 1 ? 0 : B : 255))
      .toString(16).slice(1);
  };

  const abrirModalTodasCitas = (fecha, citas) => {
    setFechaDiaSeleccionado(fecha);
    setCitasDelDiaSeleccionado(citas);
    setShowTodasCitasModal(true);
  };

  // Funciones de aprobación/rechazo
  const abrirModalAprobar = (cita) => {
    setCitaSeleccionada(cita);
    setShowAprobarModal(true);
  };

  const abrirModalAprobarConCambios = (cita) => {
    setCitaSeleccionada(cita);
    setCambiosCita({
      fecha: cita.fecha,
      hora_inicio: cita.hora_inicio,
      hora_fin: cita.hora_fin,
      medico_id: cita.medico_id || '',
      consulta_id: cita.consulta_id || ''
    });
    setShowAprobarConCambiosModal(true);
  };

  const abrirModalRechazar = (cita) => {
    setCitaSeleccionada(cita);
    setMotivoRechazo('');
    setShowRechazarModal(true);
  };

  const aprobarCita = async () => {
    if (!citaSeleccionada) return;

    try {
      const token = localStorage.getItem('token');
      // CORRECCIÓN: Cambiar de /citas/:id/aprobar a /citas/citas/:id/aprobar
      const response = await fetch(
        `${API_URL}/api/citas/citas/${citaSeleccionada.id}/aprobar`,
        {
          method: 'POST',
          headers: { 
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Error al aprobar la cita');
      }

      alert('✓ Cita aprobada correctamente');
      setShowAprobarModal(false);
      setCitaSeleccionada(null);
      await cargarCitasPendientes();
      await cargarCitas();
      await cargarPacientes();
    } catch (err) {
      console.error('Error al aprobar cita:', err);
      alert(`Error: ${err.message}`);
    }
  };

  const aprobarCitaConCambios = async (e) => {
    e.preventDefault();
    if (!citaSeleccionada) return;

    try {
      const token = localStorage.getItem('token');
      // CORRECCIÓN: Cambiar de /citas/:id/aprobar a /citas/citas/:id/aprobar
      const response = await fetch(
        `${API_URL}/api/citas/citas/${citaSeleccionada.id}/aprobar`,
        {
          method: 'POST',
          headers: { 
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            cambios: {
              fecha: cambiosCita.fecha,
              hora_inicio: cambiosCita.hora_inicio,
              hora_fin: cambiosCita.hora_fin,
              medico_id: cambiosCita.medico_id || null,
              consulta_id: cambiosCita.consulta_id || null
            }
          })
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Error al aprobar la cita');
      }

      alert('✓ Cita aprobada con cambios correctamente');
      setShowAprobarConCambiosModal(false);
      setCitaSeleccionada(null);
      await cargarCitasPendientes();
      await cargarCitas();
      await cargarPacientes();
    } catch (err) {
      console.error('Error al aprobar cita con cambios:', err);
      alert(`Error: ${err.message}`);
    }
  };

  const rechazarCita = async (e) => {
    e.preventDefault();
    if (!citaSeleccionada) return;

    if (!motivoRechazo.trim()) {
      alert('Por favor, ingrese un motivo de rechazo');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      // CORRECCIÓN: Cambiar de /citas/:id/rechazar a /citas/citas/:id/rechazar
      const response = await fetch(
        `${API_URL}/api/citas/citas/${citaSeleccionada.id}/rechazar`,
        {
          method: 'POST',
          headers: { 
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ motivo_rechazo: motivoRechazo })
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Error al rechazar la cita');
      }

      alert('✓ Cita rechazada correctamente');
      setShowRechazarModal(false);
      setCitaSeleccionada(null);
      setMotivoRechazo('');
      await cargarCitasPendientes();
    } catch (err) {
      console.error('Error al rechazar cita:', err);
      alert(`Error: ${err.message}`);
    }
  };

  const toggleAutoRegistro = async () => {
    setGuardandoConfiguracion(true);
    
    try {
      const token = localStorage.getItem('token');
      // CORRECCIÓN: Esta ruta está correcta en el backend como /clientes/:id/auto-registro
      const response = await fetch(
        `${API_URL}/api/citas/clientes/${clienteActual.id}/auto-registro`,
        {
          method: 'PUT',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ enabled: !autoRegistroEnabled })
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Error al actualizar configuración');
      }

      setAutoRegistroEnabled(!autoRegistroEnabled);
      
      alert(!autoRegistroEnabled 
        ? '✓ Auto-registro de pacientes activado' 
        : '✓ Auto-registro de pacientes desactivado');

    } catch (err) {
      console.error('Error al actualizar configuración:', err);
      alert(`Error: ${err.message}`);
    } finally {
      setGuardandoConfiguracion(false);
    }
  };

  const toggleRequireCitaApproval = async () => {
    setGuardandoConfiguracion(true);
    
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(
        `${API_URL}/api/citas/clientes/${clienteActual.id}/require-cita-approval`,
        {
          method: 'PUT',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ enabled: !requireCitaApproval })
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Error al actualizar configuración');
      }

      setRequireCitaApproval(!requireCitaApproval);
      
      alert(!requireCitaApproval 
        ? '✓ Aprobación de citas activada' 
        : '✓ Aprobación de citas desactivada');

    } catch (err) {
      console.error('Error al actualizar configuración:', err);
      alert(`Error: ${err.message}`);
    } finally {
      setGuardandoConfiguracion(false);
    }
  };

  // Funciones CRUD existentes
  const abrirModalCita = (fecha = null, cita = null) => {
    if (cita) {
      setCitaForm({
        id: cita.id, paciente_id: cita.paciente_id, medico_id: cita.medico_id || '',
        consulta_id: cita.consulta_id || '', fecha: cita.fecha,
        hora_inicio: cita.hora_inicio, hora_fin: cita.hora_fin, 
        motivo: cita.motivo || '', notas: cita.notas || '', estado: cita.estado
      });
    } else {
      const fechaParaForm = fecha || fechaSeleccionada;
      const year = fechaParaForm.getFullYear();
      const month = String(fechaParaForm.getMonth() + 1).padStart(2, '0');
      const day = String(fechaParaForm.getDate()).padStart(2, '0');
      const fechaStr = `${year}-${month}-${day}`;
      
      setCitaForm({
        id: null, paciente_id: '', medico_id: '', consulta_id: '', fecha: fechaStr,
        hora_inicio: '09:00', hora_fin: '10:00', motivo: '', notas: '', estado: 'programada'
      });
    }
    setShowCitaModal(true);
  };

  const guardarCita = async (e) => {
    e.preventDefault();
    if (!citaForm.paciente_id) {
      alert('Debe seleccionar un paciente');
      return;
    }
    
    try {
      const token = localStorage.getItem('token');
      const url = citaForm.id ? `${API_URL}/api/citas/citas/${citaForm.id}` : `${API_URL}/api/citas/citas`;
      const method = citaForm.id ? 'PUT' : 'POST';
      
      const response = await fetch(url, {
        method,
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...citaForm, cliente_id: clienteActual.id })
      });
      
      if (response.ok) {
        setShowCitaModal(false);
        await cargarCitas();
        await cargarEstadisticas();
      } else if (response.status === 409) {
        const data = await response.json();
        setConflictoData({
          citaOriginal: citaForm,
          horariosAlternativos: data.horariosAlternativos || []
        });
        setShowConflictoModal(true);
      } else {
        const data = await response.json();
        alert(data.message || 'Error al guardar la cita');
      }
    } catch (error) {
      console.error('Error al guardar cita:', error);
      alert('Error al guardar la cita');
    }
  };

  const guardarCitaConHorarioAlternativo = async (horario) => {
    try {
      const token = localStorage.getItem('token');
      const url = conflictoData.citaOriginal.id ? 
        `${API_URL}/api/citas/citas/${conflictoData.citaOriginal.id}` : 
        `${API_URL}/api/citas/citas`;
      const method = conflictoData.citaOriginal.id ? 'PUT' : 'POST';
      
      const citaConNuevoHorario = {
        ...conflictoData.citaOriginal,
        hora_inicio: horario.hora_inicio,
        hora_fin: horario.hora_fin,
        cliente_id: clienteActual.id
      };
      
      const response = await fetch(url, {
        method,
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(citaConNuevoHorario)
      });
      
      if (response.ok) {
        setShowConflictoModal(false);
        setShowCitaModal(false);
        setConflictoData(null);
        await cargarCitas();
        await cargarEstadisticas();
      } else {
        const data = await response.json();
        alert(data.message || 'Error al guardar la cita');
      }
    } catch (error) {
      console.error('Error al guardar cita:', error);
      alert('Error al guardar la cita');
    }
  };

  const guardarPaciente = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('token');
      const url = pacienteForm.id ? `${API_URL}/api/citas/pacientes/${pacienteForm.id}` : `${API_URL}/api/citas/pacientes`;
      const method = pacienteForm.id ? 'PUT' : 'POST';
      
      const response = await fetch(url, {
        method,
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...pacienteForm, cliente_id: clienteActual.id })
      });
      
      if (response.ok) {
        setShowPacienteModal(false);
        await cargarPacientes();
      }
    } catch (error) {
      console.error('Error al guardar paciente:', error);
    }
  };

  const guardarMedico = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('token');
      const url = medicoForm.id ? `${API_URL}/api/citas/medicos/${medicoForm.id}` : `${API_URL}/api/citas/medicos`;
      const method = medicoForm.id ? 'PUT' : 'POST';
      
      const response = await fetch(url, {
        method,
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...medicoForm, cliente_id: clienteActual.id })
      });
      
      if (response.ok) {
        setShowMedicoModal(false);
        await cargarMedicos();
      }
    } catch (error) {
      console.error('Error al guardar médico:', error);
    }
  };

  const guardarConsulta = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('token');
      const url = consultaForm.id ? `${API_URL}/api/citas/consultas/${consultaForm.id}` : `${API_URL}/api/citas/consultas`;
      const method = consultaForm.id ? 'PUT' : 'POST';
      
      const response = await fetch(url, {
        method,
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...consultaForm, cliente_id: clienteActual.id })
      });
      
      if (response.ok) {
        setShowConsultaModal(false);
        await cargarConsultas();
      }
    } catch (error) {
      console.error('Error al guardar consulta:', error);
    }
  };

  const eliminarPaciente = async (id) => {
    if (!window.confirm('¿Está seguro de eliminar este paciente?')) return;
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/citas/pacientes/${id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) await cargarPacientes();
    } catch (error) {
      console.error('Error al eliminar paciente:', error);
    }
  };

  const eliminarMedico = async (id) => {
    if (!window.confirm('¿Está seguro de eliminar este médico?')) return;
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/citas/medicos/${id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) await cargarMedicos();
    } catch (error) {
      console.error('Error al eliminar médico:', error);
    }
  };

  const cargarEstadoCalendario = async () => {
  try {
    const token = localStorage.getItem('token');
    const response = await fetch(
      `${API_URL}/api/citas/calendar/status/${clienteActual.id}`,
      { headers: { 'Authorization': `Bearer ${token}` } }
    );
    if (response.ok) {
      const data = await response.json();
      setCalendarioEstado(data);
    }
  } catch (error) {
    console.error('Error al cargar estado del calendario:', error);
  }
};

const conectarCalendario = async (tipo) => {
  try {
    setGuardandoConfiguracion(true);
    const token = localStorage.getItem('token');
    
    const response = await fetch(
      `${API_URL}/api/citas/calendar/${tipo}/auth/${clienteActual.id}`,
      { headers: { 'Authorization': `Bearer ${token}` } }
    );
    
    if (response.ok) {
      const data = await response.json();
      // Abrir ventana de autenticación
      window.open(data.authUrl, '_blank', 'width=600,height=700');
      
      // Escuchar cuando la ventana se cierra
      const checkInterval = setInterval(async () => {
        await cargarEstadoCalendario();
        if (calendarioEstado?.conectado) {
          clearInterval(checkInterval);
          alert(`✓ ${tipo} Calendar conectado correctamente`);
        }
      }, 2000);
      
      // Limpiar después de 5 minutos
      setTimeout(() => clearInterval(checkInterval), 300000);
    }
  } catch (error) {
    console.error('Error al conectar calendario:', error);
    alert('Error al iniciar la conexión con el calendario');
  } finally {
    setGuardandoConfiguracion(false);
  }
};

const conectarICloud = async (e) => {
  e.preventDefault();
  
  if (!icloudForm.email || !icloudForm.password) {
    alert('Por favor, completa todos los campos');
    return;
  }
  
  try {
    setGuardandoConfiguracion(true);
    const token = localStorage.getItem('token');
    
    const response = await fetch(
      `${API_URL}/api/citas/calendar/icloud/setup`,
      {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          clienteId: clienteActual.id,
          email: icloudForm.email,
          password: icloudForm.password
        })
      }
    );
    
    if (response.ok) {
      alert('✓ iCloud Calendar conectado correctamente');
      setShowICloudModal(false);
      setIcloudForm({ email: '', password: '' });
      await cargarEstadoCalendario();
    } else {
      const data = await response.json();
      alert(`Error: ${data.message}`);
    }
  } catch (error) {
    console.error('Error al conectar iCloud:', error);
    alert('Error al conectar iCloud Calendar');
  } finally {
    setGuardandoConfiguracion(false);
  }
};


const desconectarCalendario = async () => {
  if (!window.confirm('¿Estás seguro de desconectar el calendario? Las citas existentes no se eliminarán.')) {
    return;
  }
  
  try {
    setGuardandoConfiguracion(true);
    const token = localStorage.getItem('token');
    
    const response = await fetch(
      `${API_URL}/api/citas/calendar/disconnect/${clienteActual.id}`,
      {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      }
    );
    
    if (response.ok) {
      alert('✓ Calendario desconectado correctamente');
      await cargarEstadoCalendario();
    }
  } catch (error) {
    console.error('Error al desconectar calendario:', error);
    alert('Error al desconectar el calendario');
  } finally {
    setGuardandoConfiguracion(false);
  }
};

const eliminarConsulta = async (id) => {
    if (!window.confirm('¿Está seguro de eliminar esta consulta?')) return;
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/citas/consultas/${id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) await cargarConsultas();
    } catch (error) {
      console.error('Error al eliminar consulta:', error);
    }
  };

  const limpiarFiltros = () => {
    setFiltros({
      medico_id: '', consulta_id: '', estado: '', busqueda: '', fecha_desde: '', fecha_hasta: ''
    });
  };

  // Calcular estadísticas
  const calcularEstadisticas = () => {
    const totalPendientes = citasPendientes.length;
    const totalCitas = citas.length;
    const aprobadas = citas.filter(c => c.estado_registro === 'aprobada').length;
    
    return { totalPendientes, totalCitas, aprobadas };
  };

  if (loading && !clienteActual) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-t-4 border-green-600 mx-auto mb-4" style={{ borderTopColor: '#3cbf9a' }}></div>
          <p className="text-gray-600">Cargando información...</p>
        </div>
      </div>
    );
  }

  if (!user || !clienteActual) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <Calendar size={64} className="text-gray-400 mx-auto mb-4" />
          <h3 className="text-xl font-semibold text-gray-900 mb-2">Gestión de Citas</h3>
          <p className="text-gray-600">No se pudo cargar la información de la clínica</p>
        </div>
      </div>
    );
  }

  const stats = calcularEstadisticas();

  return (
    <div className="min-h-screen bg-gray-50 flex">
      {/* Sidebar */}
      <aside className="w-80 bg-white border-r border-gray-200 flex flex-col">
        <div className="p-6 border-b border-gray-200">
          <div className="flex items-center space-x-3 mb-4">
            <div className="p-3 rounded-full" style={{ backgroundColor: 'rgba(60, 191, 154, 0.1)' }}>
              <Building2 className="w-6 h-6" style={{ color: '#3cbf9a' }} />
            </div>
            <div className="flex-1 min-w-0">
              <h1 className="text-lg font-bold text-gray-800 truncate">{clienteActual.razon_social}</h1>
              <p className="text-xs text-gray-600">Sistema de Citas</p>
            </div>
          </div>
          
          <div className="space-y-2 mb-4 text-xs text-gray-600">
            {clienteActual.telefono1 && (
              <div className="flex items-center">
                <Phone className="w-3 h-3 mr-2 flex-shrink-0" style={{ color: '#3cbf9a' }} />
                <span className="truncate">{clienteActual.telefono1}</span>
              </div>
            )}
            {clienteActual.mail && (
              <div className="flex items-center">
                <Mail className="w-3 h-3 mr-2 flex-shrink-0" style={{ color: '#3cbf9a' }} />
                <span className="truncate">{clienteActual.mail}</span>
              </div>
            )}
            {clienteActual.direccion && (
              <div className="flex items-center">
                <MapPin className="w-3 h-3 mr-2 flex-shrink-0" style={{ color: '#3cbf9a' }} />
                <span className="truncate">{clienteActual.direccion}</span>
              </div>
            )}
          </div>
          
          <button
            onClick={() => abrirModalCita()}
            className="w-full px-4 py-3 text-white rounded-lg transition-all duration-200 flex items-center justify-center font-medium shadow-sm hover:shadow-md"
            style={{ backgroundColor: '#3cbf9a' }}
          >
            <Plus size={20} className="mr-2" />
            Nueva Cita
          </button>
        </div>

        <div className="flex-1 overflow-y-auto">
          <div className="p-4 border-b border-gray-200">
            <h3 className="text-sm font-semibold text-gray-700 mb-3 uppercase tracking-wide">Accesos Rápidos</h3>
            <div className="space-y-2">
              <button
                onClick={() => {
                  setPacienteForm({
                    id: null, nombre: '', apellidos: '', telefono: '', email: '',
                    dni: '', fecha_nacimiento: '', direccion: '', notas: ''
                  });
                  setShowPacienteModal(true);
                }}
                className="w-full text-left px-3 py-2 rounded-lg transition-all duration-200 border border-gray-200 hover:border-gray-300 bg-white hover:bg-gray-50 flex items-center"
              >
                <User size={16} className="mr-2 text-gray-500" />
                <span className="text-sm font-medium text-gray-700">Nuevo Paciente</span>
              </button>
              
              <button
                onClick={() => {
                  setMedicoForm({
                    id: null, nombre: '', apellidos: '', especialidad: '',
                    telefono: '', email: '', num_colegiado: '', color: '#3cbf9a', activo: true
                  });
                  setShowMedicoModal(true);
                }}
                className="w-full text-left px-3 py-2 rounded-lg transition-all duration-200 border border-gray-200 hover:border-gray-300 bg-white hover:bg-gray-50 flex items-center"
              >
                <Stethoscope size={16} className="mr-2 text-gray-500" />
                <span className="text-sm font-medium text-gray-700">Nuevo Médico</span>
              </button>
              
              <button
                onClick={() => {
                  setConsultaForm({ id: null, nombre: '', ubicacion: '', color: '#3cbf9a', activa: true });
                  setShowConsultaModal(true);
                }}
                className="w-full text-left px-3 py-2 rounded-lg transition-all duration-200 border border-gray-200 hover:border-gray-300 bg-white hover:bg-gray-50 flex items-center"
              >
                <MapPin size={16} className="mr-2 text-gray-500" />
                <span className="text-sm font-medium text-gray-700">Nueva Consulta</span>
              </button>
            </div>
          </div>

          <div className="p-4">
            <h3 className="text-sm font-semibold text-gray-700 mb-3 uppercase tracking-wide">Resumen</h3>
            <div className="space-y-3">
              <div className="flex items-center justify-between text-sm">
                <div className="flex items-center">
                  <AlertCircle size={14} className="mr-2 text-orange-500" />
                  <span className="text-gray-600">Pendientes</span>
                </div>
                <span className="font-semibold text-orange-600">{stats.totalPendientes}</span>
              </div>
              
              <div className="flex items-center justify-between text-sm">
                <div className="flex items-center">
                  <Users size={14} className="mr-2 text-gray-400" />
                  <span className="text-gray-600">Pacientes</span>
                </div>
                <span className="font-semibold text-gray-900">{pacientes.length}</span>
              </div>
              
              <div className="flex items-center justify-between text-sm">
                <div className="flex items-center">
                  <Stethoscope size={14} className="mr-2 text-gray-400" />
                  <span className="text-gray-600">Médicos</span>
                </div>
                <span className="font-semibold text-gray-900">{medicos.filter(m => m.activo).length}</span>
              </div>
              
              <div className="flex items-center justify-between text-sm">
                <div className="flex items-center">
                  <MapPin size={14} className="mr-2 text-gray-400" />
                  <span className="text-gray-600">Consultas</span>
                </div>
                <span className="font-semibold text-gray-900">{consultas.filter(c => c.activa).length}</span>
              </div>
            </div>
          </div>
        </div>
      </aside>

      {/* Contenido Principal */}
      <main className="flex-1 overflow-hidden flex flex-col">
        <div className="bg-white border-b border-gray-200 p-3">
          <div className="flex items-center justify-between gap-4">
            <div className="flex items-center gap-2">
              <button
                onClick={() => cambiarMes(-1)}
                className="p-1.5 rounded border border-gray-300 hover:bg-gray-50"
              >
                <ChevronLeft size={16} />
              </button>
              
              <div className="text-sm font-semibold text-gray-900 min-w-[140px] text-center" style={{ textTransform: 'capitalize' }}>
                {mesActual.toLocaleDateString('es-ES', { month: 'long', year: 'numeric' })}
              </div>
              
              <button
                onClick={() => cambiarMes(1)}
                className="p-1.5 rounded border border-gray-300 hover:bg-gray-50"
              >
                <ChevronRight size={16} />
              </button>
              
              <button
                onClick={irHoy}
                className="px-3 py-1.5 text-xs text-white rounded"
                style={{ backgroundColor: '#3cbf9a' }}
              >
                Hoy
              </button>
            </div>

            <div className="flex items-center gap-2 flex-1 justify-end">
              <Filter size={14} style={{ color: '#3cbf9a' }} />
              <select
                value={filtros.medico_id}
                onChange={(e) => setFiltros({...filtros, medico_id: e.target.value})}
                className="px-2 py-1.5 border border-gray-300 rounded text-xs focus:outline-none focus:ring-1 focus:ring-opacity-50"
                style={{ focusRingColor: '#3cbf9a' }}
              >
                <option value="">Todos los médicos</option>
                {medicos.map(m => (
                  <option key={m.id} value={m.id}>Dr. {m.nombre} {m.apellidos}</option>
                ))}
              </select>
              
              <select
                value={filtros.consulta_id}
                onChange={(e) => setFiltros({...filtros, consulta_id: e.target.value})}
                className="px-2 py-1.5 border border-gray-300 rounded text-xs focus:outline-none focus:ring-1 focus:ring-opacity-50"
                style={{ focusRingColor: '#3cbf9a' }}
              >
                <option value="">Todas las consultas</option>
                {consultas.map(c => (
                  <option key={c.id} value={c.id}>{c.nombre}</option>
                ))}
              </select>
              
              <select
                value={filtros.estado}
                onChange={(e) => setFiltros({...filtros, estado: e.target.value})}
                className="px-2 py-1.5 border border-gray-300 rounded text-xs focus:outline-none focus:ring-1 focus:ring-opacity-50"
                style={{ focusRingColor: '#3cbf9a' }}
              >
                <option value="">Todos los estados</option>
                {Object.keys(ESTADOS_CITA).map(key => (
                  <option key={key} value={key}>{ESTADOS_CITA[key].label}</option>
                ))}
              </select>
              
              {(filtros.medico_id || filtros.consulta_id || filtros.estado || filtros.fecha_desde || filtros.fecha_hasta) && (
                <button
                  onClick={limpiarFiltros}
                  className="px-3 py-1.5 text-xs text-gray-600 hover:text-gray-900 border border-gray-300 rounded"
                >
                  Limpiar
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Pestañas */}
        <div className="bg-white border-b border-gray-200">
          <div className="px-4">
            <nav className="flex space-x-6">
              <button
                onClick={() => setVistaActiva('calendario')}
                className={`py-3 px-1 border-b-2 font-medium text-sm transition-colors ${
                  vistaActiva === 'calendario'
                    ? 'text-gray-900'
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
                style={vistaActiva === 'calendario' ? { borderColor: '#3cbf9a', color: '#3cbf9a' } : {}}
              >
                <Calendar size={16} className="inline mr-2" />
                Calendario
              </button>
              
              <button
                onClick={() => setVistaActiva('pendientes')}
                className={`py-3 px-1 border-b-2 font-medium text-sm transition-colors relative ${
                  vistaActiva === 'pendientes'
                    ? 'text-gray-900'
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
                style={vistaActiva === 'pendientes' ? { borderColor: '#3cbf9a', color: '#3cbf9a' } : {}}
              >
                <AlertCircle size={16} className="inline mr-2" />
                Pendientes
                {stats.totalPendientes > 0 && (
                  <span className="ml-2 px-2 py-0.5 text-xs bg-orange-500 text-white rounded-full">
                    {stats.totalPendientes}
                  </span>
                )}
              </button>
              
              <button
                onClick={() => setVistaActiva('lista')}
                className={`py-3 px-1 border-b-2 font-medium text-sm transition-colors ${
                  vistaActiva === 'lista'
                    ? 'text-gray-900'
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
                style={vistaActiva === 'lista' ? { borderColor: '#3cbf9a', color: '#3cbf9a' } : {}}
              >
                <FileText size={16} className="inline mr-2" />
                Lista
              </button>
              
              <button
                onClick={() => setVistaActiva('pacientes')}
                className={`py-3 px-1 border-b-2 font-medium text-sm transition-colors ${
                  vistaActiva === 'pacientes'
                    ? 'text-gray-900'
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
                style={vistaActiva === 'pacientes' ? { borderColor: '#3cbf9a', color: '#3cbf9a' } : {}}
              >
                <Users size={16} className="inline mr-2" />
                Pacientes
              </button>
              
              <button
                onClick={() => setVistaActiva('medicos')}
                className={`py-3 px-1 border-b-2 font-medium text-sm transition-colors ${
                  vistaActiva === 'medicos'
                    ? 'text-gray-900'
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
                style={vistaActiva === 'medicos' ? { borderColor: '#3cbf9a', color: '#3cbf9a' } : {}}
              >
                <Stethoscope size={16} className="inline mr-2" />
                Médicos
              </button>
              
              <button
                onClick={() => setVistaActiva('consultas')}
                className={`py-3 px-1 border-b-2 font-medium text-sm transition-colors ${
                  vistaActiva === 'consultas'
                    ? 'text-gray-900'
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
                style={vistaActiva === 'consultas' ? { borderColor: '#3cbf9a', color: '#3cbf9a' } : {}}
              >
                <MapPin size={16} className="inline mr-2" />
                Consultas
              </button>
              
              <button
                onClick={() => setVistaActiva('configuracion')}
                className={`py-3 px-1 border-b-2 font-medium text-sm transition-colors ${
                  vistaActiva === 'configuracion'
                    ? 'text-gray-900'
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
                style={vistaActiva === 'configuracion' ? { borderColor: '#3cbf9a', color: '#3cbf9a' } : {}}
              >
                <Settings size={16} className="inline mr-2" />
                Configuración
              </button>
            </nav>
          </div>
        </div>

        {/* Contenido - Vistas */}
        <div className="flex-1 overflow-auto p-4">
          {/* Vista Calendario */}
          {vistaActiva === 'calendario' && (
            <div style={{ 
              display: 'grid', 
              gridTemplateColumns: 'repeat(7, 1fr)', 
              gap: '8px',
              minHeight: '600px'
            }}>
              {['Domingo', 'Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes', 'Sábado'].map(dia => (
                <div 
                  key={dia} 
                  style={{
                    padding: '12px',
                    textAlign: 'center',
                    fontWeight: '600',
                    fontSize: '0.875rem',
                    color: '#4b5563',
                    backgroundColor: '#f9fafb',
                    borderRadius: '8px 8px 0 0',
                    borderBottom: '2px solid #e5e7eb'
                  }}
                >
                  {dia}
                </div>
              ))}
              
              {obtenerDiasDelMes().map((dia, index) => {
                const citasDelDia = obtenerCitasDelDia(dia.fecha);
                const isToday = esHoy(dia.fecha);
                const mostrarBotonVerTodo = citasDelDia.length > 3;
                const citasVisibles = mostrarBotonVerTodo ? citasDelDia.slice(0, 3) : citasDelDia;
                
                return (
                  <div
                    key={index}
                    onClick={() => dia.mesActual && abrirModalCita(dia.fecha)}
                    style={{
                      backgroundColor: dia.mesActual ? '#ffffff' : '#f9fafb',
                      border: isToday ? '2px solid #3cbf9a' : '1px solid #e5e7eb',
                      borderRadius: '8px',
                      padding: '8px',
                      minHeight: '120px',
                      cursor: dia.mesActual ? 'pointer' : 'default',
                      transition: 'all 0.2s',
                      display: 'flex',
                      flexDirection: 'column',
                      position: 'relative',
                      opacity: dia.mesActual ? 1 : 0.5
                    }}
                    onMouseEnter={(e) => {
                      if (dia.mesActual) {
                        e.currentTarget.style.boxShadow = '0 4px 6px -1px rgba(0, 0, 0, 0.1)';
                        e.currentTarget.style.transform = 'translateY(-2px)';
                      }
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.boxShadow = 'none';
                      e.currentTarget.style.transform = 'translateY(0)';
                    }}
                  >
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      width: '28px',
                      height: '28px',
                      borderRadius: '50%',
                      fontSize: '0.875rem',
                      fontWeight: isToday ? '700' : '600',
                      color: isToday ? '#ffffff' : (dia.mesActual ? '#1f2937' : '#9ca3af'),
                      backgroundColor: isToday ? '#3cbf9a' : 'transparent',
                      marginBottom: '8px'
                    }}>
                      {dia.fecha.getDate()}
                    </div>
                    
                    <div style={{
                      flex: 1,
                      overflowY: 'hidden',
                      overflowX: 'hidden',
                      display: 'flex',
                      flexDirection: 'column',
                      gap: '4px',
                      paddingRight: '2px'
                    }}>
                      {citasVisibles.map(cita => {
                        const color = getColorCita(cita);
                        const bgColor = lightenColor(color, 85);
                        
                        return (
                          <div
                            key={cita.id}
                            onClick={(e) => {
                              e.stopPropagation();
                              setCitaSeleccionada(cita);
                              setShowDetalleModal(true);
                            }}
                            style={{
                              backgroundColor: bgColor,
                              borderLeft: `3px solid ${color}`,
                              borderRadius: '4px',
                              padding: '6px 8px',
                              fontSize: '0.75rem',
                              transition: 'all 0.2s',
                              cursor: 'pointer',
                              minHeight: '48px',
                              display: 'flex',
                              flexDirection: 'column',
                              gap: '2px'
                            }}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.backgroundColor = lightenColor(color, 75);
                              e.currentTarget.style.transform = 'translateX(2px)';
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.backgroundColor = bgColor;
                              e.currentTarget.style.transform = 'translateX(0)';
                            }}
                          >
                            <div style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: '4px',
                              fontWeight: '600',
                              color: color,
                              fontSize: '0.7rem'
                            }}>
                              <Clock size={10} />
                              <span>{cita.hora_inicio}</span>
                            </div>
                            
                            <div style={{
                              fontWeight: '500',
                              color: '#374151',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                              lineHeight: '1.2'
                            }}>
                              {cita.paciente_nombre}
                            </div>
                            
                            {(cita.medico_nombre || cita.consulta_nombre) && (
                              <div style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '4px',
                                color: '#6b7280',
                                fontSize: '0.65rem',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap'
                              }}>
                                {cita.medico_nombre && (
                                  <>
                                    <Stethoscope size={8} />
                                    <span>Dr. {cita.medico_nombre}</span>
                                  </>
                                )}
                                {cita.consulta_nombre && (
                                  <>
                                    <MapPin size={8} />
                                    <span>{cita.consulta_nombre}</span>
                                  </>
                                )}
                              </div>
                            )}
                            
                            {cita.estado !== 'programada' && (
                              <div style={{
                                display: 'inline-block',
                                padding: '2px 6px',
                                borderRadius: '10px',
                                fontSize: '0.6rem',
                                fontWeight: '600',
                                backgroundColor: ESTADOS_CITA[cita.estado].color,
                                color: '#ffffff',
                                alignSelf: 'flex-start',
                                marginTop: '2px'
                              }}>
                                {ESTADOS_CITA[cita.estado].label}
                              </div>
                            )}
                          </div>
                        );
                      })}
                      
                      {mostrarBotonVerTodo && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            abrirModalTodasCitas(dia.fecha, citasDelDia);
                          }}
                          style={{
                            padding: '6px 8px',
                            borderRadius: '4px',
                            fontSize: '0.7rem',
                            fontWeight: '600',
                            color: '#3cbf9a',
                            backgroundColor: 'rgba(60, 191, 154, 0.1)',
                            border: '1px solid #3cbf9a',
                            cursor: 'pointer',
                            transition: 'all 0.2s',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: '4px',
                            marginTop: '4px'
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.backgroundColor = 'rgba(60, 191, 154, 0.2)';
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.backgroundColor = 'rgba(60, 191, 154, 0.1)';
                          }}
                        >
                          <MoreHorizontal size={14} />
                          Ver todo ({citasDelDia.length})
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* Vista Pendientes */}
          {vistaActiva === 'pendientes' && (
          <div className="space-y-4">
            {/* Header */}
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <div className="p-2 rounded-full bg-orange-100">
                  <AlertCircle size={18} className="text-orange-600" />
                </div>
                <div>
                  <h2 className="text-lg font-bold text-gray-900">
                    Solicitudes Pendientes
                  </h2>
                  <p className="text-xs text-gray-500">
                    {citasPendientes.length === 0 
                      ? 'No hay solicitudes' 
                      : `${citasPendientes.length} solicitud${citasPendientes.length !== 1 ? 'es' : ''} pendiente${citasPendientes.length !== 1 ? 's' : ''}`}
                  </p>
                </div>
              </div>
              <button
                onClick={cargarCitasPendientes}
                className="flex items-center gap-1.5 px-3 py-1.5 text-xs border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
              >
                <RefreshCw size={14} />
                Actualizar
              </button>
            </div>

            {citasPendientes.length === 0 ? (
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8">
                <div className="text-center">
                  <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-3">
                    <AlertCircle className="w-8 h-8 text-gray-400" />
                  </div>
                  <h3 className="text-base font-semibold text-gray-900 mb-1">
                    No hay solicitudes pendientes
                  </h3>
                  <p className="text-gray-500 text-xs max-w-md mx-auto">
                    Las nuevas solicitudes aparecerán aquí
                  </p>
                </div>
              </div>
            ) : (
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th colSpan="3" className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider border-r border-gray-300">
                          <div className="flex items-center gap-2">
                            <User size={14} style={{ color: '#3cbf9a' }} />
                            Datos del Paciente
                          </div>
                        </th>
                        <th colSpan="3" className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                          <div className="flex items-center gap-2">
                            <Calendar size={14} style={{ color: '#3cbf9a' }} />
                            Datos de la Cita
                          </div>
                        </th>
                        <th className="px-4 py-3 text-center text-xs font-semibold text-gray-700 uppercase tracking-wider">
                          Acciones
                        </th>
                      </tr>
                      <tr>
                        {/* Columnas Paciente */}
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-600 border-r border-gray-300">
                          Nombre
                        </th>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-600 border-r border-gray-300">
                          DNI / Teléfono
                        </th>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-600 border-r border-gray-300">
                          Email
                        </th>
                        
                        {/* Columnas Cita */}
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-600">
                          Fecha / Hora
                        </th>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-600">
                          Médico / Consulta
                        </th>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-600">
                          Motivo
                        </th>
                        
                        {/* Acciones */}
                        <th className="px-4 py-2"></th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      {citasPendientes.map(cita => {
                        const esPacienteNuevo = cita.paciente_estado === 'pendiente';
                        
                        return (
                          <tr 
                            key={cita.id} 
                            style={{ 
                              backgroundColor: esPacienteNuevo ? '#fef3c7' : '#d1fae5'
                            }}
                            className="hover:opacity-90 transition-opacity"
                          >
                            {/* SECCIÓN PACIENTE */}
                            
                            {/* Nombre */}
                            <td 
                              className="px-4 py-3 border-r"
                              style={{ borderColor: esPacienteNuevo ? '#fcd34d' : '#6ee7b7' }}
                            >
                              <div className="flex items-center gap-2">
                                <div 
                                  className="w-7 h-7 rounded-full flex items-center justify-center text-white text-[10px] font-bold flex-shrink-0" 
                                  style={{ backgroundColor: esPacienteNuevo ? '#f59e0b' : '#10b981' }}
                                >
                                  {cita.paciente_nombre?.charAt(0)}{cita.paciente_apellidos?.charAt(0)}
                                </div>
                                <div className="min-w-0">
                                  <p className="text-xs font-semibold text-gray-900 truncate">
                                    {cita.paciente_nombre} {cita.paciente_apellidos}
                                  </p>
                                  <p className="text-[10px] text-gray-600">
                                    {new Date(cita.created_at).toLocaleDateString('es-ES', { 
                                      day: '2-digit', 
                                      month: 'short',
                                      hour: '2-digit',
                                      minute: '2-digit'
                                    })}
                                  </p>
                                </div>
                              </div>
                            </td>
                            
                            {/* DNI / Teléfono */}
                            <td 
                              className="px-4 py-3 border-r"
                              style={{ borderColor: esPacienteNuevo ? '#fcd34d' : '#6ee7b7' }}
                            >
                              <div className="space-y-1">
                                <div className="flex items-center gap-1.5">
                                  <FileText size={10} className="text-gray-500 flex-shrink-0" />
                                  <span className="text-xs font-medium text-gray-900">{cita.paciente_dni}</span>
                                </div>
                                <div className="flex items-center gap-1.5">
                                  <Phone size={10} className="text-gray-500 flex-shrink-0" />
                                  <span className="text-xs text-gray-700">{cita.paciente_telefono}</span>
                                </div>
                              </div>
                            </td>
                            
                            {/* Email */}
                            <td 
                              className="px-4 py-3 border-r"
                              style={{ borderColor: esPacienteNuevo ? '#fcd34d' : '#6ee7b7' }}
                            >
                              <div className="flex items-center gap-1.5">
                                <Mail size={10} className="text-gray-500 flex-shrink-0" />
                                <span className="text-xs text-gray-900 truncate max-w-[180px]" title={cita.paciente_email}>
                                  {cita.paciente_email}
                                </span>
                              </div>
                            </td>
                            
                            {/* SECCIÓN CITA */}
                            
                            {/* Fecha / Hora */}
                            <td className="px-4 py-3">
                              <div className="space-y-1">
                                <div className="flex items-center gap-1.5">
                                  <Calendar size={10} className="text-gray-500 flex-shrink-0" />
                                  <span className="text-xs font-medium text-gray-900">
                                    {new Date(cita.fecha).toLocaleDateString('es-ES', { 
                                      day: 'numeric', 
                                      month: 'short',
                                      year: 'numeric'
                                    })}
                                  </span>
                                </div>
                                <div className="flex items-center gap-1.5">
                                  <Clock size={10} className="text-gray-500 flex-shrink-0" />
                                  <span className="text-xs text-gray-700">
                                    {cita.hora_inicio} - {cita.hora_fin}
                                  </span>
                                </div>
                              </div>
                            </td>
                            
                            {/* Médico / Consulta */}
                            <td className="px-4 py-3">
                              <div className="space-y-1">
                                {cita.medico_nombre ? (
                                  <div className="flex items-center gap-1.5">
                                    <Stethoscope size={10} className="text-gray-500 flex-shrink-0" />
                                    <span className="text-xs text-gray-900">Dr. {cita.medico_nombre}</span>
                                  </div>
                                ) : (
                                  <span className="text-xs text-gray-400 italic">Sin médico</span>
                                )}
                                {cita.consulta_nombre ? (
                                  <div className="flex items-center gap-1.5">
                                    <MapPin size={10} className="text-gray-500 flex-shrink-0" />
                                    <span className="text-xs text-gray-700">{cita.consulta_nombre}</span>
                                  </div>
                                ) : (
                                  <span className="text-xs text-gray-400 italic">Sin consulta</span>
                                )}
                              </div>
                            </td>
                            
                            {/* Motivo */}
                            <td className="px-4 py-3">
                              {cita.motivo ? (
                                <p className="text-xs text-gray-700 line-clamp-2 max-w-[200px]" title={cita.motivo}>
                                  {cita.motivo}
                                </p>
                              ) : (
                                <span className="text-xs text-gray-400 italic">Sin motivo</span>
                              )}
                            </td>
                            
                            {/* Acciones */}
                            <td className="px-4 py-3">
                              <div className="flex items-center justify-center gap-1.5">
                                <button
                                  onClick={() => abrirModalRechazar(cita)}
                                  className="p-1.5 text-red-600 hover:bg-red-50 rounded transition-colors"
                                  title="Rechazar"
                                >
                                  <XCircle size={16} />
                                </button>
                                <button
                                  onClick={() => abrirModalAprobarConCambios(cita)}
                                  className="p-1.5 hover:bg-green-50 rounded transition-colors"
                                  style={{ color: '#3cbf9a' }}
                                  title="Aprobar con cambios"
                                >
                                  <Edit size={16} />
                                </button>
                                <button
                                  onClick={() => abrirModalAprobar(cita)}
                                  className="p-1.5 text-white rounded transition-opacity hover:opacity-80"
                                  style={{ backgroundColor: '#3cbf9a' }}
                                  title="Aprobar"
                                >
                                  <CheckCircle size={16} />
                                </button>
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
                
                {/* Leyenda */}
                <div className="px-4 py-3 bg-gray-50 border-t border-gray-200">
                  <div className="flex items-center justify-between text-xs">
                    <div className="flex items-center gap-4">
                      <div className="flex items-center gap-2">
                        <div className="w-4 h-4 rounded" style={{ backgroundColor: '#fef3c7', border: '1px solid #fcd34d' }}></div>
                        <span className="text-gray-600">Paciente Nuevo - Pendiente de Aprobación</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <div className="w-4 h-4 rounded" style={{ backgroundColor: '#d1fae5', border: '1px solid #6ee7b7' }}></div>
                        <span className="text-gray-600">Paciente Registrado - Activo</span>
                      </div>
                    </div>
                    <div className="text-gray-500">
                      Total: {citasPendientes.length} solicitud{citasPendientes.length !== 1 ? 'es' : ''}
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

          {/* Vista Configuración */}
          {vistaActiva === 'configuracion' && (
            <div className="max-w-7xl mx-auto">
              {/* Header */}
              <div className="mb-6">
                <h1 className="text-2xl font-bold text-gray-900 mb-1">
                  Configuración del Sistema
                </h1>
                <p className="text-sm text-gray-600">
                  Personaliza cómo funciona el sistema de citas para tu clínica
                </p>
              </div>

              {/* Tabs de Configuración */}
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 mb-5">
                <div className="border-b border-gray-200">
                  <nav className="flex -mb-px">
                    <button
                      onClick={() => setConfigTab('general')}
                      className={`px-6 py-3 text-sm font-medium border-b-2 transition-colors ${
                        configTab === 'general'
                          ? 'border-[#3cbf9a] text-[#3cbf9a]'
                          : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                      }`}
                    >
                      General
                    </button>
                    <button
                      onClick={() => setConfigTab('calendario')}
                      className={`px-6 py-3 text-sm font-medium border-b-2 transition-colors ${
                        configTab === 'calendario'
                          ? 'border-[#3cbf9a] text-[#3cbf9a]'
                          : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                      }`}
                    >
                      <Calendar size={16} className="inline mr-2" />
                      Calendario
                    </button>
                  </nav>
                </div>
              </div>

              {/* Contenido de Tabs */}
              {configTab === 'general' && (
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
                  
                  {/* Card 1: Auto-Registro */}
                  <div className="bg-white rounded-xl shadow-sm border border-gray-200 hover:shadow-md transition-shadow">
                    <div className="p-5">
                      {/* Header */}
                      <div className="flex items-center justify-between mb-4">
                        <div className="flex items-center gap-3">
                          <div className="p-2 rounded-lg bg-blue-50 flex items-center justify-center">
                            <UserCheck size={22} className="text-blue-600" />
                          </div>
                          <div>
                            <h3 className="text-base font-semibold text-gray-900">
                              Auto-Registro de Pacientes
                            </h3>
                            <p className="text-xs text-gray-500 mt-0.5">
                              Control de registro público
                            </p>
                          </div>
                        </div>
                        
                        {/* Toggle */}
                        <button
                          onClick={toggleAutoRegistro}
                          disabled={guardandoConfiguracion}
                          className={`relative inline-flex items-center h-7 w-12 flex-shrink-0 cursor-pointer rounded-full transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-offset-2 ${
                            autoRegistroEnabled 
                              ? 'bg-green-600 focus:ring-green-500' 
                              : 'bg-gray-300 focus:ring-gray-400'
                          } ${guardandoConfiguracion ? 'opacity-50 cursor-not-allowed' : ''}`}
                        >
                          <span className="sr-only">Activar auto-registro</span>
                          <span
                            className={`inline-flex items-center justify-center h-6 w-6 transform rounded-full bg-white shadow-md transition-transform duration-200 ease-in-out ${
                              autoRegistroEnabled ? 'translate-x-5' : 'translate-x-0.5'
                            }`}
                          >
                            {autoRegistroEnabled ? (
                              <CheckCircle size={14} className="text-green-600" />
                            ) : (
                              <XCircle size={14} className="text-gray-400" />
                            )}
                          </span>
                        </button>
                      </div>

                      {/* Descripción */}
                      <p className="text-sm text-gray-600 leading-relaxed mb-3">
                        Permite que nuevos pacientes se registren y soliciten citas desde el calendario público. 
                        <strong className="text-gray-800"> Las solicitudes quedarán pendientes de aprobación.</strong>
                      </p>

                      {/* Estado */}
                      <div className={`px-3 py-2.5 rounded-lg border-l-3 ${
                        autoRegistroEnabled 
                          ? 'bg-green-50 border-green-500' 
                          : 'bg-gray-50 border-gray-400'
                      }`}>
                        <div className="flex items-start gap-2">
                          <div className="flex-shrink-0 mt-0.5">
                            {autoRegistroEnabled ? (
                              <CheckCircle className="h-4 w-4 text-green-600" />
                            ) : (
                              <AlertCircle className="h-4 w-4 text-gray-500" />
                            )}
                          </div>
                          <div>
                            <h4 className={`text-xs font-semibold mb-0.5 ${
                              autoRegistroEnabled ? 'text-green-900' : 'text-gray-700'
                            }`}>
                              {autoRegistroEnabled ? '✓ Activado' : '✗ Desactivado'}
                            </h4>
                            <p className={`text-xs leading-relaxed ${
                              autoRegistroEnabled ? 'text-green-800' : 'text-gray-600'
                            }`}>
                              {autoRegistroEnabled 
                                ? 'Nuevos pacientes pueden solicitar citas. Aparecerán en "Pendientes".'
                                : 'Solo pacientes ya registrados pueden solicitar citas.'}
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Card 2: Aprobación de Citas */}
                  <div className="bg-white rounded-xl shadow-sm border border-gray-200 hover:shadow-md transition-shadow">
                    <div className="p-5">
                      {/* Header */}
                      <div className="flex items-center justify-between mb-4">
                        <div className="flex items-center gap-3">
                          <div className="p-2 rounded-lg bg-purple-50 flex items-center justify-center">
                            <ClipboardCheck size={22} className="text-purple-600" />
                          </div>
                          <div>
                            <h3 className="text-base font-semibold text-gray-900">
                              Aprobación de Citas
                            </h3>
                            <p className="text-xs text-gray-500 mt-0.5">
                              Control de pacientes registrados
                            </p>
                          </div>
                        </div>
                        
                        {/* Toggle */}
                        <button
                          onClick={toggleRequireCitaApproval}
                          disabled={guardandoConfiguracion}
                          className={`relative inline-flex items-center h-7 w-12 flex-shrink-0 cursor-pointer rounded-full transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-offset-2 ${
                            requireCitaApproval 
                              ? 'bg-purple-600 focus:ring-purple-500' 
                              : 'bg-gray-300 focus:ring-gray-400'
                          } ${guardandoConfiguracion ? 'opacity-50 cursor-not-allowed' : ''}`}
                        >
                          <span className="sr-only">Requerir aprobación</span>
                          <span
                            className={`inline-flex items-center justify-center h-6 w-6 transform rounded-full bg-white shadow-md transition-transform duration-200 ease-in-out ${
                              requireCitaApproval ? 'translate-x-5' : 'translate-x-0.5'
                            }`}
                          >
                            {requireCitaApproval ? (
                              <Shield size={14} className="text-purple-600" />
                            ) : (
                              <CheckCircle size={14} className="text-blue-600" />
                            )}
                          </span>
                        </button>
                      </div>

                      {/* Descripción */}
                      <p className="text-sm text-gray-600 leading-relaxed mb-3">
                        Controla si las citas de <strong className="text-gray-800">pacientes ya registrados</strong> requieren 
                        tu aprobación o se crean automáticamente en el calendario.
                      </p>

                      {/* Estado */}
                      <div className={`px-3 py-2.5 rounded-lg border-l-3 ${
                        requireCitaApproval 
                          ? 'bg-purple-50 border-purple-500' 
                          : 'bg-blue-50 border-blue-500'
                      }`}>
                        <div className="flex items-start gap-2">
                          <div className="flex-shrink-0 mt-0.5">
                            {requireCitaApproval ? (
                              <Shield className="h-4 w-4 text-purple-600" />
                            ) : (
                              <CheckCircle className="h-4 w-4 text-blue-600" />
                            )}
                          </div>
                          <div>
                            <h4 className={`text-xs font-semibold mb-0.5 ${
                              requireCitaApproval ? 'text-purple-900' : 'text-blue-900'
                            }`}>
                              {requireCitaApproval ? '🔒 Modo Manual' : '⚡ Modo Automático'}
                            </h4>
                            <p className={`text-xs leading-relaxed ${
                              requireCitaApproval ? 'text-purple-800' : 'text-blue-800'
                            }`}>
                              {requireCitaApproval 
                                ? 'Las citas requieren tu aprobación antes de confirmarse.'
                                : 'Las citas se confirman automáticamente sin revisión.'}
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                </div>
              )}

              {/* Tab de Calendario */}
              {configTab === 'calendario' && (
                <div className="space-y-5">
                  
                  {/* Información del Estado Actual */}
                  {calendarioEstado && (
                    <div className={`p-4 rounded-lg border ${
                      calendarioEstado.conectado 
                        ? 'bg-green-50 border-green-200' 
                        : 'bg-blue-50 border-blue-200'
                    }`}>
                      <div className="flex items-start gap-3">
                        <div className={`flex-shrink-0 p-2 rounded-full ${
                          calendarioEstado.conectado ? 'bg-green-100' : 'bg-blue-100'
                        }`}>
                          {calendarioEstado.conectado ? (
                            <CheckCircle className="h-5 w-5 text-green-600" />
                          ) : (
                            <Calendar className="h-5 w-5 text-blue-600" />
                          )}
                        </div>
                        <div className="flex-1">
                          <h4 className={`text-sm font-semibold mb-1 ${
                            calendarioEstado.conectado ? 'text-green-900' : 'text-blue-900'
                          }`}>
                            {calendarioEstado.conectado 
                              ? `Calendario ${calendarioEstado.tipo_calendario === 'google' ? 'Google' : calendarioEstado.tipo_calendario === 'outlook' ? 'Outlook' : 'iCloud'} Conectado` 
                              : 'No hay calendario conectado'}
                          </h4>
                          <p className={`text-xs ${
                            calendarioEstado.conectado ? 'text-green-700' : 'text-blue-700'
                          }`}>
                            {calendarioEstado.conectado 
                              ? `Sincronizando automáticamente con: ${calendarioEstado.email_calendario}` 
                              : 'Conecta un calendario para sincronizar automáticamente tus citas médicas'}
                          </p>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Título de sección */}
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">
                      Plataformas de Calendario
                    </h3>
                    <p className="text-sm text-gray-600">
                      Elige una plataforma para sincronizar tus citas automáticamente
                    </p>
                  </div>

                  {/* Grid de Opciones de Calendario */}
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
                    
                    {/* Google Calendar */}
                    <div className={`bg-white rounded-xl shadow-sm border-2 transition-all ${
                      calendarioEstado?.conectado && calendarioEstado?.tipo_calendario === 'google'
                        ? 'border-green-500 ring-2 ring-green-100'
                        : 'border-gray-200 hover:border-blue-300'
                    }`}>
                      <div className="p-6">
                        {/* Header con icono */}
                        <div className="flex items-start justify-between mb-4">
                          <div className="flex items-center gap-3">
                            <div className="p-3 rounded-xl bg-gradient-to-br from-blue-50 to-blue-100">
                              <Calendar className="w-7 h-7 text-blue-600" />
                            </div>
                            <div>
                              <h3 className="text-base font-bold text-gray-900">Google Calendar</h3>
                              <p className="text-xs text-gray-500 mt-0.5">Gmail & Google Workspace</p>
                            </div>
                          </div>
                          {calendarioEstado?.conectado && calendarioEstado?.tipo_calendario === 'google' && (
                            <div className="flex-shrink-0">
                              <CheckCircle className="w-5 h-5 text-green-600" />
                            </div>
                          )}
                        </div>

                        {/* Características */}
                        <div className="mb-4 space-y-2">
                          <div className="flex items-center gap-2 text-xs text-gray-600">
                            <CheckCircle className="w-3.5 h-3.5 text-green-500" />
                            <span>Sincronización en tiempo real</span>
                          </div>
                          <div className="flex items-center gap-2 text-xs text-gray-600">
                            <CheckCircle className="w-3.5 h-3.5 text-green-500" />
                            <span>Compatible con Gmail</span>
                          </div>
                          <div className="flex items-center gap-2 text-xs text-gray-600">
                            <CheckCircle className="w-3.5 h-3.5 text-green-500" />
                            <span>Notificaciones automáticas</span>
                          </div>
                        </div>
                        
                        {/* Botón de acción */}
                        {calendarioEstado?.conectado && calendarioEstado?.tipo_calendario === 'google' ? (
                          <div className="space-y-2">
                            <div className="px-3 py-2 bg-green-50 rounded-lg border border-green-200">
                              <div className="flex items-center gap-2">
                                <Mail className="w-3.5 h-3.5 text-green-600" />
                                <span className="text-xs font-medium text-green-900 truncate">
                                  {calendarioEstado.email_calendario}
                                </span>
                              </div>
                            </div>
                            <button
                              onClick={desconectarCalendario}
                              disabled={guardandoConfiguracion}
                              className="w-full px-4 py-2.5 text-sm font-medium bg-red-50 text-red-700 rounded-lg hover:bg-red-100 transition-colors disabled:opacity-50 border border-red-200"
                            >
                              <div className="flex items-center justify-center gap-2">
                                <X className="w-4 h-4" />
                                Desconectar
                              </div>
                            </button>
                          </div>
                        ) : (
                          <button
                            onClick={() => conectarCalendario('google')}
                            disabled={guardandoConfiguracion || calendarioEstado?.conectado}
                            className="w-full px-4 py-2.5 text-sm font-medium text-white rounded-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed hover:shadow-md"
                            style={{ 
                              backgroundColor: calendarioEstado?.conectado ? '#9ca3af' : '#3b82f6',
                              ...(calendarioEstado?.conectado ? {} : { boxShadow: '0 2px 4px rgba(59, 130, 246, 0.2)' })
                            }}
                          >
                            <div className="flex items-center justify-center gap-2">
                              {calendarioEstado?.conectado ? (
                                <>
                                  <Shield className="w-4 h-4" />
                                  Otro calendario activo
                                </>
                              ) : (
                                <>
                                  <Calendar className="w-4 h-4" />
                                  Conectar Google
                                </>
                              )}
                            </div>
                          </button>
                        )}
                      </div>
                    </div>

                    {/* Outlook Calendar */}
                    <div className={`bg-white rounded-xl shadow-sm border-2 transition-all ${
                      calendarioEstado?.conectado && calendarioEstado?.tipo_calendario === 'outlook'
                        ? 'border-green-500 ring-2 ring-green-100'
                        : 'border-gray-200 hover:border-blue-300'
                    }`}>
                      <div className="p-6">
                        {/* Header con icono */}
                        <div className="flex items-start justify-between mb-4">
                          <div className="flex items-center gap-3">
                            <div className="p-3 rounded-xl bg-gradient-to-br from-indigo-50 to-indigo-100">
                              <Calendar className="w-7 h-7 text-indigo-600" />
                            </div>
                            <div>
                              <h3 className="text-base font-bold text-gray-900">Outlook Calendar</h3>
                              <p className="text-xs text-gray-500 mt-0.5">Microsoft 365 & Outlook</p>
                            </div>
                          </div>
                          {calendarioEstado?.conectado && calendarioEstado?.tipo_calendario === 'outlook' && (
                            <div className="flex-shrink-0">
                              <CheckCircle className="w-5 h-5 text-green-600" />
                            </div>
                          )}
                        </div>

                        {/* Características */}
                        <div className="mb-4 space-y-2">
                          <div className="flex items-center gap-2 text-xs text-gray-600">
                            <CheckCircle className="w-3.5 h-3.5 text-green-500" />
                            <span>Integración Microsoft 365</span>
                          </div>
                          <div className="flex items-center gap-2 text-xs text-gray-600">
                            <CheckCircle className="w-3.5 h-3.5 text-green-500" />
                            <span>Compatible con Teams</span>
                          </div>
                          <div className="flex items-center gap-2 text-xs text-gray-600">
                            <CheckCircle className="w-3.5 h-3.5 text-green-500" />
                            <span>Recordatorios integrados</span>
                          </div>
                        </div>
                        
                        {/* Botón de acción */}
                        {calendarioEstado?.conectado && calendarioEstado?.tipo_calendario === 'outlook' ? (
                          <div className="space-y-2">
                            <div className="px-3 py-2 bg-green-50 rounded-lg border border-green-200">
                              <div className="flex items-center gap-2">
                                <Mail className="w-3.5 h-3.5 text-green-600" />
                                <span className="text-xs font-medium text-green-900 truncate">
                                  {calendarioEstado.email_calendario}
                                </span>
                              </div>
                            </div>
                            <button
                              onClick={desconectarCalendario}
                              disabled={guardandoConfiguracion}
                              className="w-full px-4 py-2.5 text-sm font-medium bg-red-50 text-red-700 rounded-lg hover:bg-red-100 transition-colors disabled:opacity-50 border border-red-200"
                            >
                              <div className="flex items-center justify-center gap-2">
                                <X className="w-4 h-4" />
                                Desconectar
                              </div>
                            </button>
                          </div>
                        ) : (
                          <button
                            onClick={() => conectarCalendario('outlook')}
                            disabled={guardandoConfiguracion || calendarioEstado?.conectado}
                            className="w-full px-4 py-2.5 text-sm font-medium text-white rounded-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed hover:shadow-md"
                            style={{ 
                              backgroundColor: calendarioEstado?.conectado ? '#9ca3af' : '#6366f1',
                              ...(calendarioEstado?.conectado ? {} : { boxShadow: '0 2px 4px rgba(99, 102, 241, 0.2)' })
                            }}
                          >
                            <div className="flex items-center justify-center gap-2">
                              {calendarioEstado?.conectado ? (
                                <>
                                  <Shield className="w-4 h-4" />
                                  Otro calendario activo
                                </>
                              ) : (
                                <>
                                  <Calendar className="w-4 h-4" />
                                  Conectar Outlook
                                </>
                              )}
                            </div>
                          </button>
                        )}
                      </div>
                    </div>

                    {/* iCloud Calendar */}
                    <div className={`bg-white rounded-xl shadow-sm border-2 transition-all ${
                      calendarioEstado?.conectado && calendarioEstado?.tipo_calendario === 'icloud'
                        ? 'border-green-500 ring-2 ring-green-100'
                        : 'border-gray-200 hover:border-gray-400'
                    }`}>
                      <div className="p-6">
                        {/* Header con icono */}
                        <div className="flex items-start justify-between mb-4">
                          <div className="flex items-center gap-3">
                            <div className="p-3 rounded-xl bg-gradient-to-br from-gray-50 to-gray-100">
                              <Cloud className="w-7 h-7 text-gray-700" />
                            </div>
                            <div>
                              <h3 className="text-base font-bold text-gray-900">iCloud Calendar</h3>
                              <p className="text-xs text-gray-500 mt-0.5">Apple iCloud & iOS</p>
                            </div>
                          </div>
                          {calendarioEstado?.conectado && calendarioEstado?.tipo_calendario === 'icloud' && (
                            <div className="flex-shrink-0">
                              <CheckCircle className="w-5 h-5 text-green-600" />
                            </div>
                          )}
                        </div>

                        {/* Características */}
                        <div className="mb-4 space-y-2">
                          <div className="flex items-center gap-2 text-xs text-gray-600">
                            <CheckCircle className="w-3.5 h-3.5 text-green-500" />
                            <span>Compatible con iOS/macOS</span>
                          </div>
                          <div className="flex items-center gap-2 text-xs text-gray-600">
                            <CheckCircle className="w-3.5 h-3.5 text-green-500" />
                            <span>Sincronización Apple</span>
                          </div>
                          <div className="flex items-center gap-2 text-xs text-gray-600">
                            <AlertCircle className="w-3.5 h-3.5 text-amber-500" />
                            <span>Requiere contraseña de app</span>
                          </div>
                        </div>
                        
                        {/* Botón de acción */}
                        {calendarioEstado?.conectado && calendarioEstado?.tipo_calendario === 'icloud' ? (
                          <div className="space-y-2">
                            <div className="px-3 py-2 bg-green-50 rounded-lg border border-green-200">
                              <div className="flex items-center gap-2">
                                <Mail className="w-3.5 h-3.5 text-green-600" />
                                <span className="text-xs font-medium text-green-900 truncate">
                                  {calendarioEstado.email_calendario}
                                </span>
                              </div>
                            </div>
                            <button
                              onClick={desconectarCalendario}
                              disabled={guardandoConfiguracion}
                              className="w-full px-4 py-2.5 text-sm font-medium bg-red-50 text-red-700 rounded-lg hover:bg-red-100 transition-colors disabled:opacity-50 border border-red-200"
                            >
                              <div className="flex items-center justify-center gap-2">
                                <X className="w-4 h-4" />
                                Desconectar
                              </div>
                            </button>
                          </div>
                        ) : (
                          <button
                            onClick={() => setShowICloudModal(true)}
                            disabled={guardandoConfiguracion || calendarioEstado?.conectado}
                            className="w-full px-4 py-2.5 text-sm font-medium text-white rounded-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed hover:shadow-md"
                            style={{ 
                              backgroundColor: calendarioEstado?.conectado ? '#9ca3af' : '#1f2937',
                              ...(calendarioEstado?.conectado ? {} : { boxShadow: '0 2px 4px rgba(31, 41, 55, 0.2)' })
                            }}
                          >
                            <div className="flex items-center justify-center gap-2">
                              {calendarioEstado?.conectado ? (
                                <>
                                  <Shield className="w-4 h-4" />
                                  Otro calendario activo
                                </>
                              ) : (
                                <>
                                  <Cloud className="w-4 h-4" />
                                  Conectar iCloud
                                </>
                              )}
                            </div>
                          </button>
                        )}
                      </div>
                    </div>

                  </div>

                  {/* Información de ayuda mejorada */}
                  <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-xl p-5">
                    <div className="flex items-start gap-4">
                      <div className="flex-shrink-0 p-2 bg-blue-100 rounded-lg">
                        <Activity className="h-5 w-5 text-blue-600" />
                      </div>
                      <div className="flex-1">
                        <h4 className="text-sm font-bold text-blue-900 mb-2">
                          ¿Cómo funciona la sincronización automática?
                        </h4>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                          <div className="flex items-start gap-2">
                            <CheckCircle className="w-4 h-4 text-blue-600 flex-shrink-0 mt-0.5" />
                            <p className="text-xs text-blue-800">
                              <strong>Creación:</strong> Las citas nuevas aparecen automáticamente en tu calendario
                            </p>
                          </div>
                          <div className="flex items-start gap-2">
                            <RefreshCw className="w-4 h-4 text-blue-600 flex-shrink-0 mt-0.5" />
                            <p className="text-xs text-blue-800">
                              <strong>Actualizaciones:</strong> Los cambios se sincronizan en tiempo real
                            </p>
                          </div>
                          <div className="flex items-start gap-2">
                            <Trash2 className="w-4 h-4 text-blue-600 flex-shrink-0 mt-0.5" />
                            <p className="text-xs text-blue-800">
                              <strong>Cancelaciones:</strong> Las citas canceladas se eliminan del calendario
                            </p>
                          </div>
                          <div className="flex items-start gap-2">
                            <Shield className="w-4 h-4 text-blue-600 flex-shrink-0 mt-0.5" />
                            <p className="text-xs text-blue-800">
                              <strong>Exclusividad:</strong> Solo un calendario puede estar conectado
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                </div>
              )}
            </div>
          )}

          {/* Vista Lista */}
          {vistaActiva === 'lista' && (
            <>
              <div className="bg-white rounded-lg shadow p-4 mb-4">
                <div className="flex items-center gap-4">
                  <div className="flex items-center gap-2">
                    <label className="text-sm font-medium text-gray-700">Desde:</label>
                    <input
                      type="date"
                      value={filtros.fecha_desde}
                      onChange={(e) => setFiltros({...filtros, fecha_desde: e.target.value})}
                      className="px-3 py-1.5 border border-gray-300 rounded text-sm focus:outline-none focus:ring-1"
                      style={{ focusRingColor: '#3cbf9a' }}
                    />
                  </div>
                  
                  <div className="flex items-center gap-2">
                    <label className="text-sm font-medium text-gray-700">Hasta:</label>
                    <input
                      type="date"
                      value={filtros.fecha_hasta}
                      onChange={(e) => setFiltros({...filtros, fecha_hasta: e.target.value})}
                      className="px-3 py-1.5 border border-gray-300 rounded text-sm focus:outline-none focus:ring-1"
                      style={{ focusRingColor: '#3cbf9a' }}
                    />
                  </div>
                </div>
              </div>
              
              <div className="bg-white rounded-lg shadow overflow-hidden">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Fecha</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Hora</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Paciente</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Médico</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Consulta</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Estado</th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Acciones</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {citas.map(cita => (
                      <tr key={cita.id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap text-sm">
                          {formatearFechaLocal(cita.fecha)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm">
                          {cita.hora_inicio} - {cita.hora_fin}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm font-medium">{cita.paciente_nombre} {cita.paciente_apellidos}</div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm">
                          {cita.medico_nombre ? `Dr. ${cita.medico_nombre}` : '-'}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm">
                          {cita.consulta_nombre || '-'}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`px-2 py-1 text-xs rounded-full ${ESTADOS_CITA[cita.estado].bgColor} ${ESTADOS_CITA[cita.estado].textColor}`}>
                            {ESTADOS_CITA[cita.estado].label}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm">
                          <button
                            onClick={() => abrirModalCita(null, cita)}
                            className="mr-3"
                            style={{ color: '#3cbf9a' }}
                          >
                            <Edit size={16} />
                          </button>
                          <button
                            onClick={() => {
                              setCitaSeleccionada(cita);
                              setShowDetalleModal(true);
                            }}
                            className="text-gray-600"
                          >
                            <Eye size={16} />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                
                {citas.length === 0 && (
                  <div className="text-center py-12">
                    <Calendar className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                    <p className="text-gray-500">No hay citas para mostrar</p>
                  </div>
                )}
              </div>
            </>
          )}

          {/* Vista Pacientes */}
          {vistaActiva === 'pacientes' && (
            <div className="bg-white rounded-lg shadow overflow-hidden">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Nombre</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">DNI</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Teléfono</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Email</th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Acciones</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {pacientes.map(paciente => (
                    <tr key={paciente.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium">{paciente.nombre} {paciente.apellidos}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm">{paciente.dni || '-'}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm">{paciente.telefono || '-'}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm">{paciente.email || '-'}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm">
                        <button
                          onClick={() => {
                            setPacienteForm(paciente);
                            setShowPacienteModal(true);
                          }}
                          className="mr-3"
                          style={{ color: '#3cbf9a' }}
                        >
                          <Edit size={16} />
                        </button>
                        <button
                          onClick={() => eliminarPaciente(paciente.id)}
                          className="text-red-600"
                        >
                          <Trash2 size={16} />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Vista Médicos */}
          {vistaActiva === 'medicos' && (
            <div className="bg-white rounded-lg shadow overflow-hidden">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Nombre</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Especialidad</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Teléfono</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Estado</th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Acciones</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {medicos.map(medico => (
                    <tr key={medico.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium">Dr. {medico.nombre} {medico.apellidos}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm">{medico.especialidad || '-'}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm">{medico.telefono || '-'}</td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`px-2 py-1 text-xs rounded-full ${medico.activo ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}`}>
                          {medico.activo ? 'Activo' : 'Inactivo'}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm">
                        <button
                          onClick={() => {
                            setMedicoForm(medico);
                            setShowMedicoModal(true);
                          }}
                          className="mr-3"
                          style={{ color: '#3cbf9a' }}
                        >
                          <Edit size={16} />
                        </button>
                        <button
                          onClick={() => eliminarMedico(medico.id)}
                          className="text-red-600"
                        >
                          <Trash2 size={16} />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Vista Consultas */}
          {vistaActiva === 'consultas' && (
            <div className="bg-white rounded-lg shadow overflow-hidden">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Nombre</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Ubicación</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Estado</th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Acciones</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {consultas.map(consulta => (
                    <tr key={consulta.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium">{consulta.nombre}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm">{consulta.ubicacion || '-'}</td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`px-2 py-1 text-xs rounded-full ${consulta.activa ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}`}>
                          {consulta.activa ? 'Activa' : 'Inactiva'}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm">
                        <button
                          onClick={() => {
                            setConsultaForm(consulta);
                            setShowConsultaModal(true);
                          }}
                          className="mr-3"
                          style={{ color: '#3cbf9a' }}
                        >
                          <Edit size={16} />
                        </button>
                        <button
                          onClick={() => eliminarConsulta(consulta.id)}
                          className="text-red-600"
                        >
                          <Trash2 size={16} />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </main>

{/* MODALES - Todos los modales existentes + nuevos */}

{showICloudModal && (
  <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
    <div className="bg-white rounded-lg max-w-md w-full">
      <div className="bg-gray-800 text-white p-4 rounded-t-lg flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Calendar size={24} />
          <h2 className="text-lg font-bold">Conectar iCloud Calendar</h2>
        </div>
        <button 
          onClick={() => {
            setShowICloudModal(false);
            setIcloudForm({ email: '', password: '' });
          }} 
          className="text-white hover:text-gray-200"
        >
          <X size={24} />
        </button>
      </div>
      
      <form onSubmit={conectarICloud} className="p-6">
        <div className="mb-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
          <p className="text-xs text-yellow-800 mb-2">
            <strong>⚠️ Importante:</strong> Necesitas una contraseña específica de app de iCloud.
          </p>
          <a 
            href="https://appleid.apple.com/" 
            target="_blank" 
            rel="noopener noreferrer"
            className="text-xs text-blue-600 hover:underline"
          >
            → Generar contraseña en appleid.apple.com
          </a>
        </div>

        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Email de iCloud *
            </label>
            <input
              type="email"
              value={icloudForm.email}
              onChange={(e) => setIcloudForm({...icloudForm, email: e.target.value})}
              className="w-full px-3 py-2 border rounded-lg focus:ring-2"
              style={{ focusRingColor: '#3cbf9a' }}
              placeholder="tu-email@icloud.com"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Contraseña Específica de App *
            </label>
            <input
              type="password"
              value={icloudForm.password}
              onChange={(e) => setIcloudForm({...icloudForm, password: e.target.value})}
              className="w-full px-3 py-2 border rounded-lg focus:ring-2"
              style={{ focusRingColor: '#3cbf9a' }}
              placeholder="xxxx-xxxx-xxxx-xxxx"
              required
            />
            <p className="text-xs text-gray-500 mt-1">
              Esta NO es tu contraseña normal de iCloud
            </p>
          </div>
        </div>

        <div className="flex gap-3 mt-6">
          <button
            type="button"
            onClick={() => {
              setShowICloudModal(false);
              setIcloudForm({ email: '', password: '' });
            }}
            className="flex-1 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
          >
            Cancelar
          </button>
          <button
            type="submit"
            disabled={guardandoConfiguracion}
            className="flex-1 px-4 py-2 bg-gray-800 text-white rounded-lg hover:bg-gray-900 disabled:opacity-50"
          >
            {guardandoConfiguracion ? 'Conectando...' : 'Conectar'}
          </button>
        </div>
      </form>
    </div>
  </div>
)}

{/* Modal Aprobar */}
{showAprobarModal && citaSeleccionada && (
  <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
    <div className="bg-white rounded-lg max-w-lg w-full">
      <div className="bg-green-600 text-white p-4 rounded-t-lg flex items-center justify-between">
        <div className="flex items-center gap-2">
          <CheckCircle size={24} />
          <h2 className="text-lg font-bold">Aprobar Solicitud</h2>
        </div>
        <button onClick={() => setShowAprobarModal(false)} className="text-white hover:text-gray-200">
          <X size={24} />
        </button>
      </div>
      
      <div className="p-6">
        <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg">
          <p className="text-green-800 font-medium mb-2">¿Confirmar aprobación?</p>
          <p className="text-green-700 text-sm">
            Se creará el paciente automáticamente y la cita será sincronizada con tu calendario.
          </p>
        </div>

        <div className="mb-6 p-4 bg-gray-50 rounded-lg text-sm">
          <p className="mb-1"><strong>Paciente:</strong> {citaSeleccionada.paciente_nombre} {citaSeleccionada.paciente_apellidos}</p>
          <p className="mb-1"><strong>DNI:</strong> {citaSeleccionada.paciente_dni}</p>
          <p className="mb-1"><strong>Fecha:</strong> {formatearFechaLocal(citaSeleccionada.fecha)}</p>
          <p><strong>Hora:</strong> {citaSeleccionada.hora_inicio} - {citaSeleccionada.hora_fin}</p>
        </div>

        <div className="flex gap-3">
          <button
            onClick={() => setShowAprobarModal(false)}
            className="flex-1 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
          >
            Cancelar
          </button>
          <button
            onClick={aprobarCita}
            className="flex-1 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 flex items-center justify-center gap-2"
          >
            <CheckCircle size={18} />
            Aprobar
          </button>
        </div>
      </div>
    </div>
  </div>
)}

{/* Modal Aprobar con Cambios */}
{showAprobarConCambiosModal && citaSeleccionada && (
  <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
    <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
      <div className="text-white p-4 rounded-t-lg flex items-center justify-between" style={{ backgroundColor: '#3cbf9a' }}>
        <div className="flex items-center gap-2">
          <Edit size={24} />
          <h2 className="text-lg font-bold">Aprobar con Cambios</h2>
        </div>
        <button onClick={() => setShowAprobarConCambiosModal(false)} className="text-white hover:text-gray-200">
          <X size={24} />
        </button>
      </div>
      
      <form onSubmit={aprobarCitaConCambios} className="p-6">
        <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
          <p className="text-blue-800 font-medium mb-2">Modifica los datos si es necesario</p>
          <p className="text-blue-700 text-sm">
            Al aprobar, se creará el paciente en el sistema con los datos proporcionados.
          </p>
        </div>

        <div className="mb-6 p-4 bg-gray-50 rounded-lg text-sm">
        <strong>Paciente:</strong> {citaSeleccionada.paciente_nombre} {citaSeleccionada.paciente_apellidos} ({citaSeleccionada.paciente_dni})
        </div>

        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Fecha *</label>
            <input
              type="date"
              value={cambiosCita.fecha}
              onChange={(e) => setCambiosCita({...cambiosCita, fecha: e.target.value})}
              className="w-full px-3 py-2 border rounded-lg focus:ring-2"
              style={{ focusRingColor: '#3cbf9a' }}
              required
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Hora Inicio *</label>
              <input
                type="time"
                value={cambiosCita.hora_inicio}
                onChange={(e) => setCambiosCita({...cambiosCita, hora_inicio: e.target.value})}
                className="w-full px-3 py-2 border rounded-lg focus:ring-2"
                style={{ focusRingColor: '#3cbf9a' }}
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Hora Fin *</label>
              <input
                type="time"
                value={cambiosCita.hora_fin}
                onChange={(e) => setCambiosCita({...cambiosCita, hora_fin: e.target.value})}
                className="w-full px-3 py-2 border rounded-lg focus:ring-2"
                style={{ focusRingColor: '#3cbf9a' }}
                required
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Médico</label>
            <select
              value={cambiosCita.medico_id}
              onChange={(e) => setCambiosCita({...cambiosCita, medico_id: e.target.value})}
              className="w-full px-3 py-2 border rounded-lg focus:ring-2"
              style={{ focusRingColor: '#3cbf9a' }}
            >
              <option value="">Sin asignar</option>
              {medicos.map(m => (
                <option key={m.id} value={m.id}>Dr. {m.nombre} {m.apellidos}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Consulta</label>
            <select
              value={cambiosCita.consulta_id}
              onChange={(e) => setCambiosCita({...cambiosCita, consulta_id: e.target.value})}
              className="w-full px-3 py-2 border rounded-lg focus:ring-2"
              style={{ focusRingColor: '#3cbf9a' }}
            >
              <option value="">Sin asignar</option>
              {consultas.map(c => (
                <option key={c.id} value={c.id}>{c.nombre}</option>
              ))}
            </select>
          </div>
        </div>

        <div className="flex gap-3 mt-6">
          <button
            type="button"
            onClick={() => setShowAprobarConCambiosModal(false)}
            className="flex-1 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
          >
            Cancelar
          </button>
          <button
            type="submit"
            className="flex-1 px-4 py-2 text-white rounded-lg hover:opacity-90 flex items-center justify-center gap-2"
            style={{ backgroundColor: '#3cbf9a' }}
          >
            <CheckCircle size={18} />
            Aprobar con Cambios
          </button>
        </div>
      </form>
    </div>
  </div>
)}

{/* Modal Rechazar */}
{showRechazarModal && citaSeleccionada && (
  <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
    <div className="bg-white rounded-lg max-w-lg w-full">
      <div className="bg-red-600 text-white p-4 rounded-t-lg flex items-center justify-between">
        <div className="flex items-center gap-2">
          <XCircle size={24} />
          <h2 className="text-lg font-bold">Rechazar Solicitud</h2>
        </div>
        <button onClick={() => setShowRechazarModal(false)} className="text-white hover:text-gray-200">
          <X size={24} />
        </button>
      </div>
      
      <form onSubmit={rechazarCita} className="p-6">
        <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
          <p className="text-red-800 font-medium mb-2">¿Confirmar rechazo?</p>
          <p className="text-red-700 text-sm">
            El paciente será notificado del rechazo. Por favor, proporciona un motivo.
          </p>
        </div>

        <div className="mb-6 p-4 bg-gray-50 rounded-lg text-sm">
        <p><strong>Paciente:</strong> {citaSeleccionada.paciente_nombre} {citaSeleccionada.paciente_apellidos}</p>
        <p><strong>DNI:</strong> {citaSeleccionada.paciente_dni}</p>
        </div>

        <div className="mb-6">
          <label className="block text-sm font-medium text-gray-700 mb-1">Motivo del Rechazo *</label>
          <textarea
            value={motivoRechazo}
            onChange={(e) => setMotivoRechazo(e.target.value)}
            placeholder="Explique por qué se rechaza esta solicitud..."
            className="w-full px-3 py-2 border rounded-lg focus:ring-2 min-h-[100px]"
            style={{ focusRingColor: '#3cbf9a' }}
            required
          />
        </div>

        <div className="flex gap-3">
          <button
            type="button"
            onClick={() => setShowRechazarModal(false)}
            className="flex-1 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
          >
            Cancelar
          </button>
          <button
            type="submit"
            className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 flex items-center justify-center gap-2"
          >
            <XCircle size={18} />
            Rechazar
          </button>
        </div>
      </form>
    </div>
  </div>
)}

{/* MODALES EXISTENTES - Mantienen su estructura original */}
{/* Modal Todas las Citas del Día - Se mantiene igual que en el código original */}
{showTodasCitasModal && (
  <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
    <div className="bg-white rounded-lg max-w-3xl w-full flex flex-col" style={{ maxHeight: '85vh', height: '85vh' }}>
      <div className="p-4 flex-shrink-0" style={{ backgroundColor: '#3cbf9a' }}>
        <div className="flex items-center justify-between text-white">
          <h2 className="text-lg font-bold">
            Citas del {fechaDiaSeleccionado && formatearFechaLocal(
              `${fechaDiaSeleccionado.getFullYear()}-${String(fechaDiaSeleccionado.getMonth() + 1).padStart(2, '0')}-${String(fechaDiaSeleccionado.getDate()).padStart(2, '0')}`
            )}
          </h2>
          <button 
            onClick={() => {
              setShowTodasCitasModal(false);
              setCitasDelDiaSeleccionado([]);
              setFechaDiaSeleccionado(null);
            }} 
            className="text-white hover:text-gray-200"
          >
            <X size={24} />
          </button>
        </div>
      </div>
      
      <div className="flex-1 overflow-y-auto p-6">
        {citasDelDiaSeleccionado.length > 0 ? (
          <div className="space-y-3">
            {citasDelDiaSeleccionado.map(cita => {
              const color = getColorCita(cita);
              const bgColor = lightenColor(color, 90);
              
              return (
                <div
                  key={cita.id}
                  onClick={() => {
                    setShowTodasCitasModal(false);
                    setCitaSeleccionada(cita);
                    setShowDetalleModal(true);
                  }}
                  style={{
                    backgroundColor: bgColor,
                    borderLeft: `4px solid ${color}`,
                    borderRadius: '8px',
                    padding: '16px',
                    cursor: 'pointer',
                    transition: 'all 0.2s'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = lightenColor(color, 80);
                    e.currentTarget.style.transform = 'translateX(4px)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = bgColor;
                    e.currentTarget.style.transform = 'translateX(0)';
                  }}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <div className="flex items-center gap-2" style={{ color: color }}>
                          <Clock size={16} />
                          <span className="font-bold text-base">
                            {cita.hora_inicio} - {cita.hora_fin}
                          </span>
                        </div>
                        <span className={`px-2 py-1 text-xs rounded-full ${ESTADOS_CITA[cita.estado].bgColor} ${ESTADOS_CITA[cita.estado].textColor}`}>
                          {ESTADOS_CITA[cita.estado].label}
                        </span>
                      </div>
                      
                      <div className="text-sm space-y-1">
                        <div className="flex items-center gap-2 text-gray-800 font-semibold">
                          <User size={14} />
                          <span>{cita.paciente_nombre} {cita.paciente_apellidos}</span>
                        </div>
                        
                        {cita.medico_nombre && (
                          <div className="flex items-center gap-2 text-gray-600">
                            <Stethoscope size={14} />
                            <span>Dr. {cita.medico_nombre} {cita.medico_apellidos}</span>
                          </div>
                        )}
                        
                        {cita.consulta_nombre && (
                          <div className="flex items-center gap-2 text-gray-600">
                            <MapPin size={14} />
                            <span>{cita.consulta_nombre}</span>
                          </div>
                        )}
                        
                        {cita.motivo && (
                          <div className="text-gray-700 mt-2">
                            <strong>Motivo:</strong> {cita.motivo}
                          </div>
                        )}
                      </div>
                    </div>
                    
                    <div className="flex gap-2">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setShowTodasCitasModal(false);
                          abrirModalCita(null, cita);
                        }}
                        className="p-2 rounded hover:bg-white/50 transition-colors"
                        style={{ color: color }}
                      >
                        <Edit size={16} />
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="text-center py-12">
            <Calendar className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-500">No hay citas este día</p>
          </div>
        )}
      </div>
      
      <div className="p-6 border-t flex justify-end flex-shrink-0">
        <button
          onClick={() => {
            setShowTodasCitasModal(false);
            setCitasDelDiaSeleccionado([]);
            setFechaDiaSeleccionado(null);
          }}
          className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
        >
          Cerrar
        </button>
      </div>
    </div>
  </div>
)}

{/* Modal Nueva Cita - Se mantiene igual */}
{showCitaModal && (
  <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
    <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
      <div className="text-white p-4" style={{ backgroundColor: '#3cbf9a' }}>
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-bold">{citaForm.id ? 'Editar Cita' : 'Nueva Cita'}</h2>
          <button onClick={() => setShowCitaModal(false)} className="text-white hover:text-gray-200">
            <X size={24} />
          </button>
        </div>
      </div>
      
      <form onSubmit={guardarCita} className="p-6 space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Paciente *</label>
          <select
            value={citaForm.paciente_id}
            onChange={(e) => setCitaForm({...citaForm, paciente_id: e.target.value})}
            className="w-full px-3 py-2 border rounded-lg focus:ring-2"
            style={{ focusRingColor: '#3cbf9a' }}
            required
          >
            <option value="">Seleccionar paciente</option>
            {pacientes.map(p => (
              <option key={p.id} value={p.id}>{p.nombre} {p.apellidos}</option>
            ))}
          </select>
        </div>
        
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Médico</label>
            <select
              value={citaForm.medico_id}
              onChange={(e) => setCitaForm({...citaForm, medico_id: e.target.value})}
              className="w-full px-3 py-2 border rounded-lg focus:ring-2"
              style={{ focusRingColor: '#3cbf9a' }}
            >
              <option value="">Sin asignar</option>
              {medicos.filter(m => m.activo).map(m => (
                <option key={m.id} value={m.id}>Dr. {m.nombre} {m.apellidos}</option>
              ))}
            </select>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Consulta</label>
            <select
              value={citaForm.consulta_id}
              onChange={(e) => setCitaForm({...citaForm, consulta_id: e.target.value})}
              className="w-full px-3 py-2 border rounded-lg focus:ring-2"
              style={{ focusRingColor: '#3cbf9a' }}
            >
              <option value="">Sin asignar</option>
              {consultas.filter(c => c.activa).map(c => (
                <option key={c.id} value={c.id}>{c.nombre}</option>
              ))}
            </select>
          </div>
        </div>
        
        <div className="grid grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Fecha *</label>
            <input
              type="date"
              value={citaForm.fecha}
              onChange={(e) => setCitaForm({...citaForm, fecha: e.target.value})}
              className="w-full px-3 py-2 border rounded-lg focus:ring-2"
              style={{ focusRingColor: '#3cbf9a' }}
              required
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Hora Inicio *</label>
            <input
              type="time"
              value={citaForm.hora_inicio}
              onChange={(e) => setCitaForm({...citaForm, hora_inicio: e.target.value})}
              className="w-full px-3 py-2 border rounded-lg focus:ring-2"
              style={{ focusRingColor: '#3cbf9a' }}
              required
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Hora Fin *</label>
            <input
              type="time"
              value={citaForm.hora_fin}
              onChange={(e) => setCitaForm({...citaForm, hora_fin: e.target.value})}
              className="w-full px-3 py-2 border rounded-lg focus:ring-2"
              style={{ focusRingColor: '#3cbf9a' }}
              required
            />
          </div>
        </div>
        
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Motivo</label>
          <input
            type="text"
            value={citaForm.motivo}
            onChange={(e) => setCitaForm({...citaForm, motivo: e.target.value})}
            className="w-full px-3 py-2 border rounded-lg focus:ring-2"
            style={{ focusRingColor: '#3cbf9a' }}
            placeholder="Motivo de la consulta"
          />
        </div>
        
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Estado</label>
          <select
            value={citaForm.estado}
            onChange={(e) => setCitaForm({...citaForm, estado: e.target.value})}
            className="w-full px-3 py-2 border rounded-lg focus:ring-2"
            style={{ focusRingColor: '#3cbf9a' }}
          >
            {Object.keys(ESTADOS_CITA).map(key => (
              <option key={key} value={key}>{ESTADOS_CITA[key].label}</option>
            ))}
          </select>
        </div>
        
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Notas</label>
          <textarea
            value={citaForm.notas}
            onChange={(e) => setCitaForm({...citaForm, notas: e.target.value})}
            className="w-full px-3 py-2 border rounded-lg focus:ring-2"
            style={{ focusRingColor: '#3cbf9a' }}
            rows="3"
          />
        </div>
        
        <div className="flex justify-end space-x-3 pt-4">
          <button
            type="button"
            onClick={() => setShowCitaModal(false)}
            className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
          >
            Cancelar
          </button>
          <button
            type="submit"
            className="px-4 py-2 text-white rounded-lg hover:opacity-90"
            style={{ backgroundColor: '#3cbf9a' }}
          >
            Guardar
          </button>
        </div>
      </form>
    </div>
  </div>
)}

{/* Los demás modales (Conflicto, Paciente, Médico, Consulta, Detalle) se mantienen exactamente iguales al código original */}
{/* Modal de Conflicto de Horarios */}
{showConflictoModal && conflictoData && (
  <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
    <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
      <div className="bg-orange-600 text-white p-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center">
            <AlertTriangle size={24} className="mr-2" />
            <h2 className="text-lg font-bold">Conflicto de Horario</h2>
          </div>
          <button 
            onClick={() => {
              setShowConflictoModal(false);
              setConflictoData(null);
            }} 
            className="text-white hover:text-gray-200"
          >
            <X size={24} />
          </button>
        </div>
      </div>
      
      <div className="p-6">
        <div className="mb-6">
          <p className="text-gray-700 mb-2">
            El horario de <strong>{conflictoData.citaOriginal.hora_inicio}</strong> a <strong>{conflictoData.citaOriginal.hora_fin}</strong> del <strong>{formatearFechaLocal(conflictoData.citaOriginal.fecha)}</strong> no está disponible.
          </p>
          {conflictoData.citaOriginal.medico_id && (
            <p className="text-sm text-gray-600 mb-2">
              • El médico ya tiene una cita programada en ese horario
            </p>
          )}
          {conflictoData.citaOriginal.consulta_id && (
            <p className="text-sm text-gray-600 mb-4">
              • La consulta ya está ocupada en ese horario
            </p>
          )}
          <p className="text-gray-700 font-medium mt-4 mb-2">Horarios alternativos disponibles:</p>
        </div>
        
        {conflictoData.horariosAlternativos && conflictoData.horariosAlternativos.length > 0 ? (
          <div className="space-y-2 mb-6">
            {conflictoData.horariosAlternativos.map((horario, index) => (
              <button
                key={index}
                onClick={() => guardarCitaConHorarioAlternativo(horario)}
                className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg transition-all text-left flex items-center justify-between"
                style={{ 
                  borderColor: '#e5e7eb',
                  ':hover': { borderColor: '#3cbf9a', backgroundColor: 'rgba(60, 191, 154, 0.05)' }
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.borderColor = '#3cbf9a';
                  e.currentTarget.style.backgroundColor = 'rgba(60, 191, 154, 0.05)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.borderColor = '#e5e7eb';
                  e.currentTarget.style.backgroundColor = 'transparent';
                }}
              >
                <div className="flex items-center">
                  <Clock className="mr-3" style={{ color: '#3cbf9a' }} size={20} />
                  <span className="font-medium">
                    {horario.hora_inicio} - {horario.hora_fin}
                  </span>
                </div>
                <ChevronRight className="text-gray-400" size={20} />
              </button>
            ))}
          </div>
        ) : (
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6">
            <p className="text-yellow-800">No hay horarios alternativos disponibles para este día con el mismo médico o consulta.</p>
            <p className="text-yellow-700 text-sm mt-2">Sugerencia: Intenta cambiar el médico, la consulta, o seleccionar otro día.</p>
          </div>
        )}
        
        <div className="flex justify-end space-x-3 pt-4 border-t">
          <button
            onClick={() => {
              setShowConflictoModal(false);
              setConflictoData(null);
            }}
            className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
          >
            Volver a Editar
          </button>
          <button
            onClick={() => {
              setShowConflictoModal(false);
              setShowCitaModal(false);
              setConflictoData(null);
            }}
            className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700"
          >
            Cancelar Todo
          </button>
        </div>
      </div>
    </div>
  </div>
)}

{/* Modal Nuevo Paciente */}
{showPacienteModal && (
  <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
    <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
      <div className="text-white p-4" style={{ backgroundColor: '#3cbf9a' }}>
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-bold">{pacienteForm.id ? 'Editar Paciente' : 'Nuevo Paciente'}</h2>
          <button onClick={() => setShowPacienteModal(false)} className="text-white hover:text-gray-200">
            <X size={24} />
          </button>
        </div>
      </div>
      
      <form onSubmit={guardarPaciente} className="p-6 space-y-4">
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Nombre *</label>
            <input
              type="text"
              value={pacienteForm.nombre}
              onChange={(e) => setPacienteForm({...pacienteForm, nombre: e.target.value})}
              className="w-full px-3 py-2 border rounded-lg focus:ring-2"
              style={{ focusRingColor: '#3cbf9a' }}
              required
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Apellidos *</label>
            <input
              type="text"
              value={pacienteForm.apellidos}
              onChange={(e) => setPacienteForm({...pacienteForm, apellidos: e.target.value})}
              className="w-full px-3 py-2 border rounded-lg focus:ring-2"
              style={{ focusRingColor: '#3cbf9a' }}
              required
            />
          </div>
        </div>
        
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">DNI</label>
            <input
              type="text"
              value={pacienteForm.dni}
              onChange={(e) => setPacienteForm({...pacienteForm, dni: e.target.value})}
              className="w-full px-3 py-2 border rounded-lg focus:ring-2"
              style={{ focusRingColor: '#3cbf9a' }}
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Fecha Nacimiento</label>
            <input
              type="date"
              value={pacienteForm.fecha_nacimiento}
              onChange={(e) => setPacienteForm({...pacienteForm, fecha_nacimiento: e.target.value})}
              className="w-full px-3 py-2 border rounded-lg focus:ring-2"
              style={{ focusRingColor: '#3cbf9a' }}
            />
          </div>
        </div>
        
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Teléfono</label>
            <input
              type="tel"
              value={pacienteForm.telefono}
              onChange={(e) => setPacienteForm({...pacienteForm, telefono: e.target.value})}
              className="w-full px-3 py-2 border rounded-lg focus:ring-2"
              style={{ focusRingColor: '#3cbf9a' }}
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
            <input
              type="email"
              value={pacienteForm.email}
              onChange={(e) => setPacienteForm({...pacienteForm, email: e.target.value})}
              className="w-full px-3 py-2 border rounded-lg focus:ring-2"
              style={{ focusRingColor: '#3cbf9a' }}
            />
          </div>
        </div>
        
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Dirección</label>
          <input
            type="text"
            value={pacienteForm.direccion}
            onChange={(e) => setPacienteForm({...pacienteForm, direccion: e.target.value})}
            className="w-full px-3 py-2 border rounded-lg focus:ring-2"
            style={{ focusRingColor: '#3cbf9a' }}
          />
        </div>
        
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Notas</label>
          <textarea
            value={pacienteForm.notas}
            onChange={(e) => setPacienteForm({...pacienteForm, notas: e.target.value})}
            className="w-full px-3 py-2 border rounded-lg focus:ring-2"
            style={{ focusRingColor: '#3cbf9a' }}
            rows="3"
          />
        </div>
        
        <div className="flex justify-end space-x-3 pt-4">
          <button
            type="button"
            onClick={() => setShowPacienteModal(false)}
            className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
          >
            Cancelar
          </button>
          <button
            type="submit"
            className="px-4 py-2 text-white rounded-lg hover:opacity-90"
            style={{ backgroundColor: '#3cbf9a' }}
          >
            Guardar
          </button>
        </div>
      </form>
    </div>
  </div>
)}

{/* Modal Nuevo Médico */}
{showMedicoModal && (
  <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
    <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
      <div className="text-white p-4" style={{ backgroundColor: '#3cbf9a' }}>
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-bold">{medicoForm.id ? 'Editar Médico' : 'Nuevo Médico'}</h2>
          <button onClick={() => setShowMedicoModal(false)} className="text-white hover:text-gray-200">
            <X size={24} />
          </button>
        </div>
      </div>
      
      <form onSubmit={guardarMedico} className="p-6 space-y-4">
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Nombre *</label>
            <input
              type="text"
              value={medicoForm.nombre}
              onChange={(e) => setMedicoForm({...medicoForm, nombre: e.target.value})}
              className="w-full px-3 py-2 border rounded-lg focus:ring-2"
              style={{ focusRingColor: '#3cbf9a' }}
              required
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Apellidos *</label>
            <input
              type="text"
              value={medicoForm.apellidos}
              onChange={(e) => setMedicoForm({...medicoForm, apellidos: e.target.value})}
              className="w-full px-3 py-2 border rounded-lg focus:ring-2"
              style={{ focusRingColor: '#3cbf9a' }}
              required
            />
          </div>
        </div>
        
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Especialidad</label>
            <input
              type="text"
              value={medicoForm.especialidad}
              onChange={(e) => setMedicoForm({...medicoForm, especialidad: e.target.value})}
              className="w-full px-3 py-2 border rounded-lg focus:ring-2"
              style={{ focusRingColor: '#3cbf9a' }}
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Nº Colegiado</label>
            <input
              type="text"
              value={medicoForm.num_colegiado}
              onChange={(e) => setMedicoForm({...medicoForm, num_colegiado: e.target.value})}
              className="w-full px-3 py-2 border rounded-lg focus:ring-2"
              style={{ focusRingColor: '#3cbf9a' }}
            />
          </div>
        </div>
        
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Teléfono</label>
            <input
              type="tel"
              value={medicoForm.telefono}
              onChange={(e) => setMedicoForm({...medicoForm, telefono: e.target.value})}
              className="w-full px-3 py-2 border rounded-lg focus:ring-2"
              style={{ focusRingColor: '#3cbf9a' }}
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
            <input
              type="email"
              value={medicoForm.email}
              onChange={(e) => setMedicoForm({...medicoForm, email: e.target.value})}
              className="w-full px-3 py-2 border rounded-lg focus:ring-2"
              style={{ focusRingColor: '#3cbf9a' }}
            />
          </div>
        </div>
        
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Color de Identificación</label>
            <input
              type="color"
              value={medicoForm.color}
              onChange={(e) => setMedicoForm({...medicoForm, color: e.target.value})}
              className="w-full h-10 border rounded-lg"
            />
          </div>
          
          <div className="flex items-center">
            <label className="flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={medicoForm.activo}
                onChange={(e) => setMedicoForm({...medicoForm, activo: e.target.checked})}
                className="mr-2 w-4 h-4"
              />
              <span className="text-sm font-medium text-gray-700">Activo</span>
            </label>
          </div>
        </div>
        
        <div className="flex justify-end space-x-3 pt-4">
          <button
            type="button"
            onClick={() => setShowMedicoModal(false)}
            className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
          >
            Cancelar
          </button>
          <button
            type="submit"
            className="px-4 py-2 text-white rounded-lg hover:opacity-90"
            style={{ backgroundColor: '#3cbf9a' }}
          >
            Guardar
          </button>
        </div>
      </form>
    </div>
  </div>
)}

{/* Modal Nueva Consulta */}
{showConsultaModal && (
  <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
    <div className="bg-white rounded-lg max-w-lg w-full">
      <div className="text-white p-4" style={{ backgroundColor: '#3cbf9a' }}>
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-bold">{consultaForm.id ? 'Editar Consulta' : 'Nueva Consulta'}</h2>
          <button onClick={() => setShowConsultaModal(false)} className="text-white hover:text-gray-200">
            <X size={24} />
          </button>
        </div>
      </div>
      
      <form onSubmit={guardarConsulta} className="p-6 space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Nombre *</label>
          <input
            type="text"
            value={consultaForm.nombre}
            onChange={(e) => setConsultaForm({...consultaForm, nombre: e.target.value})}
            className="w-full px-3 py-2 border rounded-lg focus:ring-2"
            style={{ focusRingColor: '#3cbf9a' }}
            required
          />
        </div>
        
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Ubicación</label>
          <input
            type="text"
            value={consultaForm.ubicacion}
            onChange={(e) => setConsultaForm({...consultaForm, ubicacion: e.target.value})}
            className="w-full px-3 py-2 border rounded-lg focus:ring-2"
            style={{ focusRingColor: '#3cbf9a' }}
          />
        </div>
        
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Color</label>
            <input
              type="color"
              value={consultaForm.color}
              onChange={(e) => setConsultaForm({...consultaForm, color: e.target.value})}
              className="w-full h-10 border rounded-lg"
            />
          </div>
          
          <div className="flex items-center">
            <label className="flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={consultaForm.activa}
                onChange={(e) => setConsultaForm({...consultaForm, activa: e.target.checked})}
                className="mr-2 w-4 h-4"
              />
              <span className="text-sm font-medium text-gray-700">Activa</span>
            </label>
          </div>
        </div>
        
        <div className="flex justify-end space-x-3 pt-4">
          <button
            type="button"
            onClick={() => setShowConsultaModal(false)}
            className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
          >
            Cancelar
          </button>
          <button
            type="submit"
            className="px-4 py-2 text-white rounded-lg hover:opacity-90"
            style={{ backgroundColor: '#3cbf9a' }}
          >
            Guardar
          </button>
        </div>
      </form>
    </div>
  </div>
)}

{/* Modal Detalle Cita */}
{showDetalleModal && citaSeleccionada && (
  <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
    <div className="bg-white rounded-lg max-w-2xl w-full">
      <div className="text-white p-4" style={{ backgroundColor: '#3cbf9a' }}>
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-bold">Detalles de la Cita</h2>
          <button onClick={() => setShowDetalleModal(false)} className="text-white hover:text-gray-200">
            <X size={24} />
          </button>
        </div>
      </div>
      
      <div className="p-6 space-y-4">
        <div>
          <h3 className="font-semibold text-gray-900 mb-2">Información del Paciente</h3>
          <p className="text-sm text-gray-700"><strong>Nombre:</strong> {citaSeleccionada.paciente_nombre} {citaSeleccionada.paciente_apellidos}</p>
          {citaSeleccionada.paciente_telefono && (
            <p className="text-sm text-gray-700"><strong>Teléfono:</strong> {citaSeleccionada.paciente_telefono}</p>
          )}
        </div>
        
        <div>
          <h3 className="font-semibold text-gray-900 mb-2">Información de la Cita</h3>
          <p className="text-sm text-gray-700"><strong>Fecha:</strong> {formatearFechaLocal(citaSeleccionada.fecha)}</p>
          <p className="text-sm text-gray-700"><strong>Horario:</strong> {citaSeleccionada.hora_inicio} - {citaSeleccionada.hora_fin}</p>
          <p className="text-sm text-gray-700">
            <strong>Estado:</strong>{' '}
            <span className={`px-2 py-1 text-xs rounded-full ${ESTADOS_CITA[citaSeleccionada.estado].bgColor} ${ESTADOS_CITA[citaSeleccionada.estado].textColor}`}>
              {ESTADOS_CITA[citaSeleccionada.estado].label}
            </span>
          </p>
          {citaSeleccionada.medico_nombre && (
            <p className="text-sm text-gray-700"><strong>Médico:</strong> Dr. {citaSeleccionada.medico_nombre} {citaSeleccionada.medico_apellidos}</p>
          )}
          {citaSeleccionada.consulta_nombre && (
            <p className="text-sm text-gray-700"><strong>Consulta:</strong> {citaSeleccionada.consulta_nombre}</p>
          )}
          {citaSeleccionada.motivo && (
            <p className="text-sm text-gray-700"><strong>Motivo:</strong> {citaSeleccionada.motivo}</p>
          )}
        </div>
        
        {citaSeleccionada.notas && (
          <div>
            <h3 className="font-semibold text-gray-900 mb-2">Notas</h3>
            <p className="text-sm text-gray-700">{citaSeleccionada.notas}</p>
          </div>
        )}
      </div>
      
      <div className="p-6 border-t flex justify-end space-x-3">
        <button
          onClick={() => setShowDetalleModal(false)}
          className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
        >
          Cerrar
        </button>
        <button
          onClick={() => {
            setShowDetalleModal(false);
            abrirModalCita(null, citaSeleccionada);
          }}
          className="px-4 py-2 text-white rounded-lg hover:opacity-90"
          style={{ backgroundColor: '#3cbf9a' }}
        >
          Editar
        </button>
      </div>
    </div>
  </div>
)}
    </div>
  );
}